import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Backdrop,
  Skeleton,
  Typography,
  FormControl,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Pagination,
  Select,
  Alert,
  Link,
  PaginationItem,
  Tooltip,
} from '@mui/material';
import dummyMarketHistoricalData from '../../assets/dummy_market_historical_data.json';
import RefreshIcon from '@mui/icons-material/Refresh';
import _debounce from 'lodash/debounce';
import { FilterVal, VisibleListingsProp } from 'src/interfaces/iproptypes';
import {
  AVAILABLE_COUNTRY,
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_TOOLTIP_BACKGROUND,
  COLOR_WHITE,
  FILTER_FLOAT_BUTTON_Z_INDEX_DESKTOP,
  exchangeRates,
  OverViewMapSectionMarketStatsTooltip,
  COLOR_RED,
  APP_BAR_Z_INDEX,
} from 'src/const';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';
import MapIcon from '@mui/icons-material/Map';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CloseIcon from '@mui/icons-material/Close';
import CompareIcon from '@mui/icons-material/Compare';
import Map from './HOC/Map';
import MainKPICard from './UtilityComponents/MainKPICard';
import './styles/styles.scss';
import {
  getDistrictInfo,
  getMarkers,
  setIndividualRevenuePotentialRange,
  setMarketDashboardFilter,
} from 'src/store/Dashboard/actions';
import { useDispatch, useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import TabBar from 'src/App/Components/Tabs/TabBar';
import {
  COLOR_PRIMARY_2,
  KPI_CARD_COLOR,
  COLOR_PRIMARY_1,
  COLOR_GREY_400,
  COLOR_PRIMARY_1_700,
  BACKGROUND_COLOR_2,
  BACKGROUND_COLOR_1,
  COLOR_GREY_800,
} from 'src/colors';
import { LoadingCard } from 'src/App/HOC/Cards';
import PropertySizeCard from 'src/App/Pages/OverviewPage/UtilityComponents/PropertySizeCard';
import PropertyTypeChannelPieChart from 'src/App/Pages/OverviewPage/UtilityComponents/PropertyTypeChannelPieChart';
import AmenityCard from 'src/App/Pages/OverviewPage/UtilityComponents/AmenityCard';
import ExtraPricingCard from 'src/App/Pages/OverviewPage/UtilityComponents/ExtraPricingCard';
import TopCitiesCard from 'src/App/Pages/OverviewPage/UtilityComponents/TopCitiesCard';
import TopCountriesCard from 'src/App/Pages/OverviewPage/UtilityComponents/TopCountriesCard';
import InternationDomesticGuestsCard from 'src/App/Pages/OverviewPage/UtilityComponents/InternationDomesticGuestsCard';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import Hosts from 'src/App/Pages/OverviewPage/UtilityComponents/Hosts';
import GuestLanguageCard from 'src/App/Pages/OverviewPage/UtilityComponents/GuestLanguageCard';
import ConfirmUnlockRegionModal from 'src/App/Modals/ConfirmUnlockRegionModal';
import { callApi } from 'src/core/callApi';
import {
  convertCurrency,
  deepCopyObject,
  formatCurrency,
  getDiscoveryMarketsURLCountryWise,
  getRedirectedDiscoveryMarketCountryCode,
  isEmpty,
  parseMessageHistoricalData,
  cacheApiCall,
  getCachedApiCall,
  updateDynamoDBForUserVerification,
  trackUserDynamicDataUpdateAttempt,
  extractVisibleListings,
  checkUserTailorMarketAccess,
  changePercentageToDecimal,
  filterActiveListings,
  removeQueryParamsFromURL,
  roundNumber,
  getFilteredListings,
  getVisibleListings,
  getBoundedOTAListings,
  getVisibleOTAListings,
  isDefaultFilter,
  titleCase,
  extractStaticListings,
  calculateHistoricalTrends,
  checkIfOverviewMarketIsSaved,
  reverseTransformFilterVals,
  calculateDashboardFilters,
  filterListingsData,
  setCookie,
} from 'src/utils/functions';
import BottomDrawer from './HOC/BottomDrawer';
import {
  processAndSortMarketLevelData,
  getHistoricalData,
  getMarketLevelAggregateData,
  getMinmaxROI,
  sortListingBasedOnKey,
} from './helper';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { InfoTooltip } from 'src/App/HOC/Tooltips';
import { ZoomButtons } from './UtilityComponents/ZoomButtons';
import NoDataCard from 'src/App/HOC/Cards/NoDataCard';
import SingleBarFilterCard from './HOC/FilterComponents/SingleBarFilterCard';
import ConfigurableFilterBarCard from './HOC/FilterComponents/ConfigurableFilterBarCard';
import SimplifiedAirbnbPointerBody from 'src/App/HOC/Cards/SimplifiedAirbnbPointerBody';
import { Bounds, Listing, Listings } from 'src/interfaces/idashboard';
import { infoIconButton, infoIconStyle } from 'src/App/Dashboards/Market/styles/style';
import { LTMMonthlyPerformanceBreakdownType } from 'src/interfaces/apiTypes';
import { IndividualListingModalV2 } from 'src/App/Modals';
import DiscoveryTab from './HOC/DiscoveryTab';
import CompareTab from './HOC/CompareTab';
import { useParams } from 'react-router-dom';
import { Filter } from 'src/App/HOC';
import { ListingsExportModal } from 'src/App/Modals/SubscriptionAddOns';
import { PaymentFulfillmentCheckModal } from 'src/App/Components/Modals';
import SeasonalityChart from './UtilityComponents/SeasonalityChart';
import TrendsChart from './UtilityComponents/TrendsChart';
import AmenitiesImpactOnRevenueChart from './UtilityComponents/AmenitiesImpactOnRevenueChart';
import SubmarketCreationModal from 'src/App/Modals/SubmarketCreationModal/SubmarketCreationModal';
import { InfoButton, TooltipedActionButton } from 'src/App/Components/Buttons';
import PerformanceHeatmap from './HOC/PerformanceHeatmap';
import ActionOptions from './UtilityComponents/ActionOptions';
import { ListingsGridLoadingCard } from 'src/App/Components/Loaders';
import { useIsDisabled, useSubmarketBlur } from 'src/hooks/useFeatureAccess';
import { useUpgradeMesageExpertFeature } from 'src/hooks/useUpgradeText';
import TooltipNew from 'src/App/HOC/Tooltips/TooltipNew';
import { logAmplitudeEvent } from 'src/utils/analytics';
import ShowAlertTextBox from 'src/App/Components/ShowAlertTextBox';
import MainKPITooltip from 'src/App/HOC/Tooltips/MainKPITooltip';
import { kpiTooltipConfig } from './configs/tooltipConfig';
import MarketComparePage from '../MarketCompare/MarketComparePage';

const MAP_SIZE_MAP_VIEW_DESKTOP = 40;

const redirectToDefaultRegionIfNeeded = (loginCountryCode, dispatch, pathname, dummyMarket) => {
  if (pathname === '/airbnb-data' || pathname === '/airbnb-data/') {
    const redirectTo = `/airbnb-data/${dummyMarket['country']}/${dummyMarket['city']}/${dummyMarket['district']}`;
    console.log('TEST', dummyMarket, redirectTo);
    window.history.pushState({}, '', redirectTo);
    dispatch(
      getDistrictInfo(
        loginCountryCode,
        dummyMarket['district'],
        dummyMarket['city'],
        dummyMarket['country']
      )
    );
  }
};

const getSelectedViewWidth = (
  view: 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' | 'searchListing',
  matches: boolean,
  isMapVisbleOnFullScreen: boolean,
  isDiscoveryAIVisible: boolean = false
) => {
  if (matches) {
    return view == 'map' || (view == 'discovery' && !isDiscoveryAIVisible) ? 100 : 0;
  } else {
    if (view == 'map') {
      return MAP_SIZE_MAP_VIEW_DESKTOP;
    } else if (view == 'discovery' && isDiscoveryAIVisible) {
      return 0;
    } else if (view == 'discovery' && isMapVisbleOnFullScreen) {
      return 0;
    } else if (view == 'compare') {
      return 0;
    } else if (view == 'advance') {
      return 0;
    } else {
      return 20;
    }
  }
};

const getMapViewHeadings = (
  text1: string,
  text2: string,
  infoTooltipContent: string | undefined = undefined
) => {
  return (
    <Grid
      item
      sx={{
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography
        variant="h5"
        className="marketTitle"
        sx={{
          fontSize: '20px',
          fontWeight: 'bold',
          position: 'relative',
          top: '5px',
          marginBottom: '20px',
        }}
      >
        {text1}: <span style={{ marginRight: '5px' }}>{text2}</span>
        {infoTooltipContent && (
          <TooltipNew
            title={''}
            content={infoTooltipContent}
            enterTouchDelay={0}
            leaveTouchDelay={6000}
          >
            <IconButton style={infoIconButton}>
              <InfoOutlinedIcon style={infoIconStyle} />
            </IconButton>
          </TooltipNew>
        )}
      </Typography>
    </Grid>
  );
};

const OverviewPage = ({
  search,
  SEODistrictName,
  SEOCountryName,
  SEOCityName,
  isLoggedIn,
  dashboardLayoutView,
  setDashboardLayoutView,
  matches,
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  currency,
  user,
  showSearchCreditDeductWarningModal,
  setShowSearchCreditDeductWarningModal,
  handleOnSampleClick,
  setSelectedSidebarIndex,
  handleMarketSidebarClick,
  handleSidebarListItemClick,
  setAdvancedTab,
  openDemoBookingForm,
  setDemoFormHeadingFor,
  dummyMarket,
  loginCountryCode,
  dataStoreTimePeriodQuery,
  dataStoreTimeGranularityQuery,
  getTokenThunk,
  tailoredRegionMarkers,
  getTailoredRegionMarkers,
  OnBoardingSearchBox,
  showHeaderOrBottomBarOnMobileView,
}) => {
  const dispatch = useDispatch();
  const params = useParams() as any;

  const userCountryCode = useSelector(state => (state as any).dashboard.userCountryCode);
  const priceTiers = useSelector(state => (state as any).dashboard.priceTiers);
  const isCachedMarketDataSample: boolean = useSelector(
    state => (state as any).dashboard.isCachedMarketDataSample
  );
  const listings = useSelector(state => (state as any).dashboard.listings);
  const otaListings = useSelector(state => (state as any).dashboard.otaListings);
  const multipleMarkersCallLoading = useSelector(
    state => (state as any).dashboard.multipleMarkersCallLoading
  );

  const staticListings = extractStaticListings(listings);
  const graph = useSelector(state => (state as any).dashboard.graph);
  const multipleBookingMarkersCallLoading = useSelector(
    state => (state as any).dashboard.multipleBookingMarkersCallLoading
  );
  const revPotentialRangeInvestment = useSelector(
    state => (state as any).dashboard.revPotentialRangeInvestment
  );
  const cachedMarketData = useSelector(state => (state as any).dashboard.cachedMarketData);
  const filteredListingsDashboard = useSelector(
    state => (state as any).dashboard.filteredListingsDashboard
  );
  const isUserAllowed = useSelector(state => (state as any).dashboard.isUserAllowed);
  const isDemoMarket = search.data.name.toLowerCase() === dummyMarket.district.toLowerCase();
  const listingsExportPaymentFailed = new URLSearchParams(window.location.search).get(
    'listings_export_payment_failed'
  );
  const listingsExportSessionID = new URLSearchParams(window.location.search).get(
    'listings_export_session_id'
  );
  const visibleMapSupply = useSelector(state => (state as any).dashboard.visibleMapSupply);

  redirectToDefaultRegionIfNeeded(
    loginCountryCode,
    dispatch,
    window.location.pathname,
    dummyMarket
  );

  const userTailorMarketAccess = checkUserTailorMarketAccess(user, search);

  const isDisabled: boolean = useIsDisabled();
  const [mapWidth, setMapWidth] = useState<0 | 20 | 40 | 100>(
    getSelectedViewWidth('dashboard', matches, false)
  );

  const [visibleMapListings, setVisibleMapListings] = useState<Listing[]>([]);
  const [mapViewportListings, setMapViewportListings] = useState<{
    count: number;
    loading: boolean;
  }>({
    count: 0,
    loading: true,
  });
  const [dynamicVisibleListings, setDynamicVisibleListings] = useState<VisibleListingsProp>({
    data: [],
    loading: false,
  });
  const [isDynamicDataInitialized, setIsDynamicDataInitialized] = useState(false);
  const [isDynamicDataBtnVisible, setIsDynamicDataBtnVisible] = useState(false);
  const [dashboardScrollPositionTop, setDashboardScrollPositionTop] = useState<number>(0);
  const [subKpiSelected, setSubKpiSelected] = useState<string>('revenue');
  const [subKpiMapViewSelected, setSubKpiMapViewSelected] = useState<string>('listings');
  const [openTop10ManagerModal, setOpenTop10ManagerModal] = useState<boolean>(false);
  const [selectedPercentileInMainKPICard, setSelectedPercentileInMainKPICard] = useState<string>(
    '50'
  );

  const [extraExportCreditsLoading, setExtraExportCreditsLoading] = useState<boolean>(false);
  const [extraExportError, setExtraExportError] = useState<boolean>(false);
  const [creditsToBeDeducted, setCreditsToBeDeducted] = useState<number>(1);

  const [marketLevelOccupancyRate, setMarketLevelOccupancyRate] = useState<number>(0);
  const [marketLevelRevenuePotential, setMarketLevelRevenuePotential] = useState<number>(0);
  const [marketLevelCleaningFee, setMarketLevelCleaningFee] = useState<number>(0);
  const [marketLevelBookingCount, setMarketLevelBookingCount] = useState<number>(0);
  const [marketLevelDailyRate, setMarketLevelDailyRate] = useState<number>(0);
  const [marketLevelActiveListings, setMarketLevelActiveListings] = useState<number>(0);
  const [isDiscoveryMapViewOpen, setIsDiscoveryMapViewOpen] = useState<boolean>(true);
  const [occupancyRateHistoricalData, setOccupancyRateHistoricalData] = useState<any>([]);
  const [revenueHistoricalData, setRevenueHistorical] = useState<any>([]);
  const [dailyRateHistoricalData, setDailyRateHistorical] = useState<any>([]);
  const [listingHistoricalData, setListingHistorical] = useState<any>([]);
  const [occupancyPast6MonthsHistoricalData, setOccupancyPast6MonthsHistorical] = useState<any>([]);
  const [revenuePast6MonthsHistoricalData, setRevenuePast6MonthsHistorical] = useState<any>([]);
  const [dailyRatePast6MonthsHistoricalData, setDailyRatePast6MonthsHistorical] = useState<any>([]);
  const [listingPast6MonthsHistoricalData, setListingPast6MonthsHistorical] = useState<any>([]);
  const [occupancyYoYChange, setOccupancyYoYChange] = useState<Number>();
  const [revenueYoYChange, setRevenueYoYChange] = useState<Number>();
  const [dailyYoYChange, setDailyYoYChange] = useState<Number>();
  const [listingYoYChange, setListingYoYChange] = useState<Number>();
  const [isMapViewMainKpiCardTooltipOpen, setIsMapViewMainKpiCardTooltipOpen] = useState<boolean[]>(
    [false, false, false, false]
  ); //occupancy, revenue, nightly, listings

  const [isScoreCardTooltipOpen, setIsScoreCardTooltipOpen] = useState<boolean>(false);
  const [isMarketCachedDataLoaded, setIsMarketCachedDataLoaded] = useState<boolean>(false);
  const [cachedHistoricalData, setCachedHistoricalData] = useState<{
    [key: string]: { [key: string]: number };
  } | null>(null);
  const [cachedMarketBedroomADR, setCachedMarketBedroomADR] = useState<any>(null);
  const [cachedLTMData, setCachedLTMData] = useState<{ [key: string]: number } | null>(null);
  const [
    cachedMonthlyLTMData,
    setCachedMonthlyLTMData,
  ] = useState<LTMMonthlyPerformanceBreakdownType | null>(null);

  const [showAddAirbnbURLModal, setShowAddAirbnbURLModal] = useState<boolean>(false);
  const [userSeachedListingManually, setUserSeachedListingManually] = useState<boolean>(false);
  const [
    confirmSaveCountryPlanMarketModalOpen,
    setConfirmSaveCountryPlanMarketModalOpen,
  ] = useState<boolean | undefined>(false);

  const [showPremiumDataDynamic, setShowPremiumDataDynamic] = useState<boolean>(true);

  const [topPerformingListingOnFocus, setTopPerformingListingOnFocus] = useState<string | null>(
    null
  );

  const [listingsGridStartIndexOffset, setListingsGridStartIndexOffset] = useState<number>(0);
  const [topPerformingListingModalOpen, setTopPerformingListingModalOpen] = useState<
    Listing | null | any[]
  >(null);

  const [discoveryMarkets, setDiscoveryMarkets] = useState<any[] | null | undefined>([]);
  const [minROI, setMinROI] = useState(0);
  const [maxROI, setmaxROI] = useState(0);

  const [isTabChanging, setIsTabChanging] = useState<boolean>(false);
  const [showDiscoveryAI, setShowDiscoveryAI] = useState<boolean>(false);

  const [isDiscoveryMarketLoading, setIsDiscoveryMarketLoading] = useState<boolean>(true);

  const [marketsToCompareList, setMarketsToCompareList] = useState<any[]>([]);

  const [discoverTabData, setDiscoveryTabData] = useState<any>([]);

  const [topRawMarketData, setTopRawMarketData] = useState<any[]>([]);
  const [allRawMarketData, setAllRawMarketData] = useState<any[]>([]);
  const [discoveryMarketType, setDiscoveryMarketType] = useState<'all' | 'top'>('all');
  const [processedMarketData, setProcessedMarketData] = useState<any[]>([]);
  const [allMarketProcessedData, setAllMarketProcessedData] = useState<any[]>([]);
  const [isMarketDataAccessible, setIsMarketDataAccessible] = useState<boolean>(false);
  const [freeTopMarketData, setFreeTopMarketData] = useState<any[]>([]);
  const [freeAllMarketData, setFreeAllMarketData] = useState<any[]>([]);
  const [freeMarketDataKey, setFreeMarketDataKey] = useState<string[]>([]);
  const subKPIcontainerRef = useRef<HTMLDivElement | null>(null);
  const [showListingsExportPage, setShowListingsExportPage] = useState<boolean>(false);
  const subKPIcontainerMapViewRef = useRef<HTMLDivElement | null>(null);
  const warningToastRef = useRef<React.ReactText | undefined>(undefined);
  const [listingSortingKey, setListingSortingKey] = useState<string>('performance');
  const [bounds, setBounds] = useState<Bounds>({} as Bounds);
  const [showSubmarketCreationModal, setShowSubmarketCreationModal] = useState(false);

  const [selectedDiscoveryCountry, setSelectedDiscoveryCountry] = useState<string>(
    params.country && Object.keys(AVAILABLE_COUNTRY).includes(params.country.toUpperCase())
      ? params.country.toUpperCase()
      : ''
  );

  const [filteredDiscoveryMarketsSet, setFilteredDiscoveryMarketsSet] = useState<Set<string>>(
    new Set<string>()
  );

  const noDataInMarket = !cachedMarketData.loading && isEmpty(cachedMarketData.data);

  const [
    showListingsExportPaymentFulfillmentCheckModal,
    setShowListingsExportPaymentFulfillmentCheckModal,
  ] = useState(false);

  const [listingsExportCheckoutStatus, setListingsExportCheckoutStatus] = useState('loading');
  const [filteredListings, setFilteredListings] = useState<string[] | null>(null);
  const [filteredOTAListings, setFilteredOTAListings] = useState<string[] | null>(null);
  const [filterApplied, setFilterApplied] = useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [topPerformingMarketOnFocus, setTopPerformingMarketOnFocus] = useState<any | null>(null);
  const [dynamicTrigger, setDynamicTrigger] = useState(false);
  const [zoom, setZoom] = useState<number>(12);

  const dynamicLoadingMapViewKPI =
    (dashboardLayoutView === 'map' || dashboardLayoutView === 'dashboard') && dynamicTrigger;

  const [filterVal, setFilterVal] = useState({
    roomType: ['all'],
    bedrooms: [100],
    bathrooms: [100],
    revenueFilter: ['all'],
    priceTierFilter: ['all'],
    accommodates: [1, 16],
    minimum_nights: [1, 30],
    number_of_reviews: 'all',
    rating: [3.9, 5],
    hostType: 'both',
    amenities: {
      pool: null,
      parking: null,
      hot_tub: null,
      gym: null,
      pets: null,
      dryer: null,
      washer: null,
      kitchen: null,
      ac: null,
      heating: null,
      wifi: null,
      tv: null,
      bbq_grill: null,
      fireplace: null,
      breakfast: null,
      beachfront: null,
      amazing_views: null,
    },
    showAllBookingComListings: false,
  } as FilterVal);
  const [filterActive, setFilterActive] = useState(false);
  const [submarketPolygonPaths, setSubmarketPolygonPaths] = useState([]);
  const [boundarySelectionModalOpen, setBoundarySelectionModalOpen] = useState(true);
  const [lastYearMarketTrend, setLastYearMarketTrend] = useState<number | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [showViewingLisingFromPaginationAlert, setShowViewingLisingFromPaginationAlert] = useState<
    'shown' | 'showing' | null
  >(null);

  const [mapHeightOnMobileView, setMapHeightOnMobileView] = useState<number>(40);

  const isSubMarketDetailsBlured = useSubmarketBlur();

  const currentMarketUrl = useRef<string>(
    window.location.pathname.includes('/markets/compare')
      ? `/airbnb-data/${dummyMarket['country']}/${dummyMarket['city']}/${dummyMarket['district']}`
      : window.location.pathname
  );

  useEffect(() => {
    if (!search.loading) {
      // Store last market searched in a cross sub domain cookie to access it from the wordpress
      setCookie('last-searched-market', window.location.pathname);
      setCookie('last-search-market-name', search.data.name);
    }
  }, [search]);

  useEffect(() => {
    let blurMarketDetails = false;
    if (isSubMarketDetailsBlured && dashboardLayoutView === 'dashboard') {
      blurMarketDetails = true;
    }

    try {
      const rightViewContainer = document.querySelector('.rightView__container');
      const rightViewContainerChildren = rightViewContainer?.querySelectorAll(':scope > div > div');

      rightViewContainerChildren &&
        Array.from(rightViewContainerChildren).forEach((element: any, index) => {
          if (index > 3) {
            element.style.filter = blurMarketDetails ? 'blur(5px)' : 'none';
            element.style.pointerEvents = blurMarketDetails ? 'none' : 'auto';
          } else {
            element.style.filter = 'none';
            element.style.pointerEvents = 'auto';
          }
        });
    } catch (err) {
      console.log('Error while bluring: ', err);
    }
  }, [isSubMarketDetailsBlured]);

  const OverviewBlurCTA = isSubMarketDetailsBlured ? (
    <>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '20px',
          top: '-150px',
          left: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          zIndex: 100,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            background: '#fffa',
            padding: '20px',
            borderRadius: '12px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Upgrade to Expert to access all this data
          </Typography>
          <Typography variant="body1">
            <br />
            ✅ Access detailed revenue analysis
            <br />
            <br />✅ Create unlimited submarkets
          </Typography>
          <br />
          <br />
          <Button
            variant="contained"
            onClick={() => handleOnSampleClick('expert')}
            sx={{ borderRadius: 900 }}
          >
            Upgrade to Expert
          </Button>
        </Box>
      </Box>
    </>
  ) : (
    <></>
  );

  const toggleDrawer = () => {
    setFilterOpen(!filterOpen);
  };

  let SEOVisibleListingsCount =
    listings.loading || !listings.data ? '' : Object.keys(listings.data).length;

  const [isCurrentMarketSaved, setIsCurrentMarketSaved] = useState<boolean>(false);

  useEffect(() => {
    const isMarketSaved = checkIfOverviewMarketIsSaved(search, user.savedMarkets);
    setIsCurrentMarketSaved(isMarketSaved);
  }, [search, user]);

  const accomodationRevenueInTooltip: any =
    marketLevelDailyRate * (marketLevelOccupancyRate / 100) * 365;
  const cleaningFeeRevenueInTooltip: any = marketLevelBookingCount * marketLevelCleaningFee;

  if ((window as any).Tawk_API) {
    (window as any).Tawk_API.onLoad = () => {
      (window as any).Tawk_API.hideWidget();
    };
  }

  if (listingsExportPaymentFailed && listingsExportPaymentFailed == 'true') {
    window.alert('Payment failed! Please try again.');
    removeQueryParamsFromURL();
  }

  const fulfillListingsExportCheckout = async () => {
    const url =
      'https://010jdq035f.execute-api.us-east-2.amazonaws.com/default/fulfillStripeCheckout';

    const config = {
      body: {
        jwt: localStorage.getItem('auth-token'),
        session_id: listingsExportSessionID,
        product: 'listings_export',
        metadata: JSON.parse(localStorage.getItem('listingsExportConfig') as string),
        // isTest: true,
      },
    };

    const res = await callApi(url, config, true, false, '', 'POST');
    console.log('res', res);
    if (res.message.status) {
      setListingsExportCheckoutStatus(res.message.status);
    } else {
      setListingsExportCheckoutStatus('failed');
    }
  };

  useEffect(() => {
    setFilterApplied(isDefaultFilter());
  }, []);

  useEffect(() => {
    if (dataStoreTimePeriodQuery && dataStoreTimeGranularityQuery) {
      setShowListingsExportPage(true);
    }
  }, []);

  useEffect(() => {
    if (listingsExportSessionID) {
      console.log('Listings export session ID: ' + listingsExportSessionID);
      setShowListingsExportPaymentFulfillmentCheckModal(true);
      fulfillListingsExportCheckout();
      removeQueryParamsFromURL();
    }
  }, []);

  useEffect(() => {
    setFilteredDiscoveryMarketsSet(
      new Set(
        discoverTabData.map(
          market => (market.district + market.city + market.countryCode) as string
        )
      )
    );
  }, [discoverTabData]);

  useEffect(() => {
    if (localStorage.getItem('openDiscovery') == 'true') {
      setDashboardLayoutView('discovery');
      localStorage.setItem('openDiscovery', 'false');
    }
  }, [localStorage.getItem('openDiscovery')]);

  useEffect(() => {
    userCountryCode &&
      selectedDiscoveryCountry == '' &&
      (Object.keys(AVAILABLE_COUNTRY).includes(userCountryCode)
        ? setSelectedDiscoveryCountry(userCountryCode)
        : setSelectedDiscoveryCountry('CA'));
  }, [userCountryCode]);

  useEffect(() => {
    if (!search.data.filters || !listings.data || !otaListings.data) {
      return;
    }

    const filterValues = reverseTransformFilterVals(search.data.filters);
    setFilterVal(filterValues);

    const [filteredListings, _] = getFilteredListings(
      listings.data,
      otaListings.data,
      filterValues,
      true
    );

    if (filteredListings.length > 0) {
      const filteredListingsData = filterListingsData(listings.data, filteredListings);
      const marketDashboardFilters = calculateDashboardFilters(filteredListingsData, isUserAllowed);
      dispatch(setMarketDashboardFilter(marketDashboardFilters as any));
    }
    setFilterActive(true);
    applyFilter();
  }, [search.data, listings.data]);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     setMarketsToCompareList([]);
  //     setIsDiscoveryMarketLoading(true);
  //     try {
  //       const TOP_MARKET_URL = getDiscoveryMarketsURLCountryWise(selectedDiscoveryCountry, 'top');
  //       const cachedTopMarketData = await getCachedApiCall(TOP_MARKET_URL);
  //       const top_market_res = cachedTopMarketData
  //         ? cachedTopMarketData.data
  //         : await callApi(TOP_MARKET_URL, {}, true);

  //       if (!cachedTopMarketData) {
  //         await cacheApiCall(TOP_MARKET_URL, top_market_res);
  //       }

  //       if (top_market_res.message.is_premium) {
  //         setTopRawMarketData(top_market_res.message.markets);
  //         setIsMarketDataAccessible(true);
  //       } else {
  //         setTopRawMarketData([
  //           ...top_market_res.message.sample_markets,
  //           ...top_market_res.message.markets,
  //         ]);
  //         setFreeTopMarketData(top_market_res.message.sample_markets);
  //       }

  //       const ALL_MARKET_URL = getDiscoveryMarketsURLCountryWise(selectedDiscoveryCountry, 'all');
  //       const cachedAllMarketData = await getCachedApiCall(ALL_MARKET_URL);
  //       const all_market_res = cachedAllMarketData
  //         ? cachedAllMarketData.data
  //         : await callApi(ALL_MARKET_URL, {}, true);

  //       if (!cachedAllMarketData) {
  //         await cacheApiCall(ALL_MARKET_URL, all_market_res);
  //       }

  //       if (all_market_res.message.is_premium) {
  //         setAllRawMarketData(all_market_res.message.markets);
  //         setIsMarketDataAccessible(true);
  //       } else {
  //         setAllRawMarketData([
  //           ...all_market_res.message.sample_markets,
  //           ...all_market_res.message.markets,
  //         ]);
  //         setFreeAllMarketData(all_market_res.message.sample_markets);
  //       }
  //       setIsDiscoveryMarketLoading(false);
  //     } catch (error) {
  //       console.error('Error fetching data:', error);
  //     }
  //     isDiscoveryMarketLoading && setIsDiscoveryMarketLoading(false);
  //   };

  //   selectedDiscoveryCountry && fetchData();
  // }, [selectedDiscoveryCountry]);

  useEffect(() => {
    let freeMarketKey: string[] = [];
    let visibleMarketData = discoveryMarketType == 'top' ? freeTopMarketData : freeAllMarketData;
    for (let data of visibleMarketData) {
      let key = `${data.district}-${data.city}-${data.country_code}`;
      freeMarketKey.push(key);
    }
    setFreeMarketDataKey(freeMarketKey);
  }, [freeTopMarketData, freeAllMarketData, discoveryMarketType]);

  useEffect(() => {
    if (topRawMarketData.length > 0 && allRawMarketData.length > 0) {
      let processedData;
      const rawData = discoveryMarketType === 'top' ? topRawMarketData : allRawMarketData;
      if (isMarketDataAccessible) {
        processedData = processAndSortMarketLevelData(rawData);
      } else {
        processedData = processAndSortMarketLevelData(rawData, true);
      }

      // Processing All market Data for compare tab
      let allMarketProcessedData = [];
      if (isMarketDataAccessible) {
        allMarketProcessedData = processAndSortMarketLevelData(allRawMarketData);
        setAllMarketProcessedData(allMarketProcessedData);
      } else {
        allMarketProcessedData = processAndSortMarketLevelData(allRawMarketData, true);
        setAllMarketProcessedData(allMarketProcessedData);
      }

      setProcessedMarketData(processedData);
      setDiscoveryMarkets(processedData);
      const [min, max] = getMinmaxROI(rawData);
      setMinROI(min);
      setmaxROI(max);
    }
  }, [topRawMarketData, allRawMarketData, discoveryMarketType]);

  useEffect(() => {
    if (!cachedMarketData.loading && cachedMarketData.data) {
      setCachedHistoricalData(cachedMarketData.data?.historical_monthly_performance);
      cachedMarketData.data?.ltm_performance &&
        setCachedLTMData(cachedMarketData.data?.ltm_performance);
      cachedMarketData.data?.ltm_performance && setIsMarketCachedDataLoaded(true);
      cachedMarketData.data?.ltm_monthly_performance_breakdown &&
        setCachedMonthlyLTMData(cachedMarketData.data.ltm_monthly_performance_breakdown);

      cachedMarketData.data?.adr_breakdown?.bedroom &&
        setCachedMarketBedroomADR(cachedMarketData.data?.adr_breakdown?.bedroom);
    }
  }, [cachedMarketData]);

  useEffect(() => {
    setMapWidth(getSelectedViewWidth(dashboardLayoutView, matches, false));
  }, [matches]);

  useEffect(() => {
    if (dashboardLayoutView === 'dashboard') {
      setMapWidth(getSelectedViewWidth('dashboard', matches, false));
      showViewingLisingFromPaginationAlert === 'showing' &&
        setShowViewingLisingFromPaginationAlert('shown');
    } else if (dashboardLayoutView === 'map') {
      setMapWidth(getSelectedViewWidth('map', matches, false));

      const mapViewTop = document.getElementById('overviewRight');
      mapViewTop && mapViewTop.scrollTo(0, 0);
    } else if (dashboardLayoutView === 'discovery') {
      !showDiscoveryAI &&
        mapWidth !== getSelectedViewWidth('discovery', matches, false) &&
        handleOnClickTabLoading(true);
      setMapWidth(getSelectedViewWidth('discovery', matches, false, showDiscoveryAI));
      window.history.pushState(
        {},
        'overview',
        `/markets/${getRedirectedDiscoveryMarketCountryCode(selectedDiscoveryCountry)}`
      );
    } else if (dashboardLayoutView === 'compare') {
      // handleOnClickTabLoading(mapWidth !== getSelectedViewWidth('compare', matches, false));
      setMapWidth(0);
    }
  }, [dashboardLayoutView, showDiscoveryAI]);

  const [blockListingExport, setBlockListingExport] = useState<boolean>(false);
  const [shouldShowMainKPILoading, setShouldShowMainKPILoading] = useState<boolean>(false);

  useEffect(() => {
    if (user && user.blockListingExport) {
      setBlockListingExport(true);
    }
    setShowDiscoveryAI(user.showDiscoveryAI);
  }, [user]);

  useEffect(() => {
    if (isLoggedIn && !isUserAllowed) {
      setTimeout(() => {
        if (user.credits > 0) {
          setShowSearchCreditDeductWarningModal(true);
        }
      }, 5020);
    }
  }, [isUserAllowed, isLoggedIn]);

  useEffect(() => {
    if (isDemoMarket) {
      setShouldShowMainKPILoading(!cachedLTMData ? true : false);
      return;
    }

    if (isDisabled) {
      setShouldShowMainKPILoading(false);
      return;
    }

    if (
      (listings.loading || !listings.data || !Object.keys(listings.data).length) &&
      !cachedLTMData
    ) {
      setShouldShowMainKPILoading(true);
    } else {
      setShouldShowMainKPILoading(false);
    }
  }, [listings.data, cachedLTMData, isDisabled, isDemoMarket]);

  const setHistoricalGraphData = (
    historicalMarketPerformanceData,
    type: 'full' | 'sample' = 'sample'
  ) => {
    const [dailyRateHistoricalData, dailyYoYChange] = getHistoricalData(
      historicalMarketPerformanceData,
      'daily'
    );

    setDailyRateHistorical(dailyRateHistoricalData);
    setDailyRatePast6MonthsHistorical(dailyRateHistoricalData.slice(-6));
    setDailyYoYChange(dailyYoYChange);

    if (type === 'full') {
      const [occupancyRateHistoricalData, occupancyYoYChange] = getHistoricalData(
        historicalMarketPerformanceData,
        'occupancy'
      );

      const [revenueHistoricalData, revenueYoYChange] = getHistoricalData(
        historicalMarketPerformanceData,
        'revenue'
      );

      const [listingHistoricalData, listingYoYChange] = getHistoricalData(
        historicalMarketPerformanceData,
        'listing'
      );

      setOccupancyRateHistoricalData(occupancyRateHistoricalData);
      setRevenueHistorical(revenueHistoricalData);
      setListingHistorical(listingHistoricalData);
      setOccupancyPast6MonthsHistorical(occupancyRateHistoricalData.slice(-6));
      setRevenuePast6MonthsHistorical(revenueHistoricalData.slice(-6));
      setListingPast6MonthsHistorical(listingHistoricalData.slice(-6));
      setOccupancyYoYChange(occupancyYoYChange);
      setRevenueYoYChange(revenueYoYChange);
      setListingYoYChange(listingYoYChange);
    }
  };

  useEffect(() => {
    const marketLevelOccupancyRate =
      dashboardLayoutView !== 'map' && cachedLTMData && selectedPercentileInMainKPICard == '50'
        ? cachedLTMData['occupancy_rate']
        : getMarketLevelAggregateData(
            dashboardLayoutView == 'map' ? dynamicVisibleListings.data : staticListings.data,
            isDisabled,
            'insights',
            'Occupancy Rate',
            currency,
            'process',
            dashboardLayoutView == 'map'
              ? changePercentageToDecimal('50')
              : changePercentageToDecimal(selectedPercentileInMainKPICard)
          );

    const marketLevelRevenue =
      dashboardLayoutView !== 'map' && cachedLTMData && selectedPercentileInMainKPICard == '50'
        ? cachedLTMData['revenue']
        : getMarketLevelAggregateData(
            dashboardLayoutView == 'map' ? dynamicVisibleListings.data : staticListings.data,
            isDisabled,
            'insights',
            'Revenue',
            currency,
            'process',
            dashboardLayoutView == 'map'
              ? changePercentageToDecimal('50')
              : changePercentageToDecimal(selectedPercentileInMainKPICard)
          );

    const marketLevelCleaningFee =
      dashboardLayoutView !== 'map' && cachedLTMData && selectedPercentileInMainKPICard == '50'
        ? cachedLTMData['cleaning_fee']
        : getMarketLevelAggregateData(
            dashboardLayoutView == 'map' ? dynamicVisibleListings.data : staticListings.data,
            isDisabled,
            'insights',
            'Cleaning Fee',
            currency,
            'process',
            dashboardLayoutView == 'map'
              ? changePercentageToDecimal('50')
              : changePercentageToDecimal(selectedPercentileInMainKPICard)
          );

    const marketLevelBookingCount =
      dashboardLayoutView !== 'map' && cachedLTMData && selectedPercentileInMainKPICard == '50'
        ? cachedLTMData['booking']
        : getMarketLevelAggregateData(
            dashboardLayoutView == 'map' ? dynamicVisibleListings.data : staticListings.data,
            isDisabled,
            'insights',
            'Booking Count',
            currency,
            'process',
            dashboardLayoutView == 'map'
              ? changePercentageToDecimal('50')
              : changePercentageToDecimal(selectedPercentileInMainKPICard)
          );

    const marketLevelDailyRate =
      dashboardLayoutView !== 'map' && cachedLTMData && selectedPercentileInMainKPICard == '50'
        ? cachedLTMData['adr']
        : getMarketLevelAggregateData(
            dashboardLayoutView == 'map' ? dynamicVisibleListings.data : staticListings.data,
            isDisabled,
            'insights',
            'Nightly Rate',
            currency,
            'process',
            dashboardLayoutView == 'map'
              ? changePercentageToDecimal('50')
              : changePercentageToDecimal(selectedPercentileInMainKPICard)
          );

    const tempStaticListings = staticListings.data.reduce((acc, listing) => {
      acc[listing['listingID']] = listing;
      return acc;
    }, {} as Listings);

    const marketLevelAvailiableListing =
      dashboardLayoutView !== 'map' && cachedLTMData
        ? cachedLTMData['supply']
        : dashboardLayoutView == 'map'
        ? (filterActiveListings(dynamicVisibleListings.data) as Listing[]).length
        : search.data.isSubmarket
        ? getFilteredListings(tempStaticListings, {}, filterVal, true)[0].length
        : (filterActiveListings(staticListings.data) as Listing[]).length;
    setMarketLevelOccupancyRate(marketLevelOccupancyRate);
    setMarketLevelRevenuePotential(marketLevelRevenue);
    setMarketLevelDailyRate(marketLevelDailyRate);
    setMarketLevelActiveListings(marketLevelAvailiableListing);
    setMarketLevelCleaningFee(marketLevelCleaningFee);
    setMarketLevelBookingCount(marketLevelBookingCount);

    if (cachedHistoricalData) {
      setHistoricalGraphData(
        cachedHistoricalData,
        cachedMarketData.data?.is_user_allowed ? 'full' : 'sample'
      );
    }
  }, [
    dynamicVisibleListings,
    cachedHistoricalData,
    cachedMarketData,
    selectedPercentileInMainKPICard,
    dashboardLayoutView,
  ]);

  const handleSubKPITabClick = (tabId: string) => {
    const element = document.getElementById(tabId);
    if (element) {
      // Create a new element above the selected element
      const newElement = document.createElement('div');
      newElement.style.height = '1px';
      newElement.style.position = 'relative';
      newElement.style.top = dashboardLayoutView === 'map' ? '-165px' : '-200px';

      // Insert the new element before the selected element
      element.parentNode && element.parentNode.insertBefore(newElement, element);

      // Scroll to the new element
      newElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      setTimeout(() => {
        newElement && newElement.remove();
      }, 300);
    }
  };

  const handleOnClickTabLoading = (isMapWidthChanging: boolean = true) => {
    setIsTabChanging(isMapWidthChanging ? true : false);
  };

  const handlePageUrlWithTabSwitching = (tab: string = '') => {
    let targetUrl = '';

    if (tab === 'compare') {
      targetUrl = '/markets/compare';
    } else {
      targetUrl = currentMarketUrl.current;
    }
    if (window.location.pathname !== targetUrl) {
      window.history.pushState({}, 'overview', targetUrl);
    }
  };

  const overviewDashboardTabs = [
    {
      label: 'Map',
      value: 'map',
      icon: <MapIcon />,
      onclick: () => {
        setMapWidth(MAP_SIZE_MAP_VIEW_DESKTOP);
        setDashboardLayoutView('map');
        logAmplitudeEvent('map_viewed_in_overview', { cta: 'navbar' });
        handlePageUrlWithTabSwitching();
      },
    },
    {
      label: 'Dashboard',
      value: 'dashboard',
      icon: <DashboardIcon />,
      onclick: () => {
        setMapWidth(getSelectedViewWidth('dashboard', matches, false));
        setDashboardLayoutView('dashboard');
        handlePageUrlWithTabSwitching();
      },
    },
    {
      label: 'Compare',
      value: 'compare',
      icon: <CompareIcon />,
      onclick: () => {
        setMapWidth(0);
        setDashboardLayoutView('compare');
        handlePageUrlWithTabSwitching('compare');
      },
    },
  ];

  const subKpiTabsMetadata = [
    {
      label: 'Revenue',
      value: 'revenue',
      onclick: () => {
        setSubKpiSelected('revenue');
        handleSubKPITabClick('revenue-tab');
      },
    },
    {
      label: 'Listings',
      value: 'listings',
      onclick: () => {
        setSubKpiSelected('listings');
        handleSubKPITabClick('listings-tab');
      },
    },
    {
      label: 'Nightly Rate',
      value: 'nightlyRate',
      onclick: () => {
        setSubKpiSelected('nightlyRate');
        handleSubKPITabClick('nightly-rate-tab');
      },
    },
    {
      label: 'Occupancy',
      value: 'occupancy',
      onclick: () => {
        setSubKpiSelected('occupancy');
        handleSubKPITabClick('occupancy-tab');
      },
    },
    {
      label: 'Host',
      value: 'host',
      onclick: () => {
        setSubKpiSelected('host');
        handleSubKPITabClick('host-tab');
      },
    },
  ];

  const isInViewport = (element: HTMLElement | null) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return rect.top <= 420;
  };

  const handleScroll = _debounce((event: Event) => {
    const target = event.target as HTMLElement;
    const scrollPosition: number = target.scrollTop;
    setDashboardScrollPositionTop(scrollPosition);

    dashboardLayoutView === 'dashboard' &&
      subKpiTabsMetadata.forEach(subKpiSection => {
        if (isInViewport(document.getElementById(`${subKpiSection.value}-tab`))) {
          setSubKpiSelected(subKpiSection.value);
        }
      });

    // Check if user has scrolled to the end
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    if (scrollPosition + clientHeight >= scrollHeight - 1) {
      // User has scrolled to the end
      logAmplitudeEvent('overview_scrolled_to_end', {
        market_id: search.data.id,
        is_submarket: search.data.isSubmarket,
      });
    }
  }, MAP_SIZE_MAP_VIEW_DESKTOP);

  const handleResizeEndDashboardView = () => {
    setIsTabChanging(false);
  };

  useEffect(() => {
    const rightViewElement = document.getElementById('overviewRight');
    rightViewElement && rightViewElement.addEventListener('scroll', handleScroll);

    // Observes the resize handling for Map Container
    const leftMapView = document.getElementById('overviewLeft');
    leftMapView && leftMapView.addEventListener('transitionend', handleResizeEndDashboardView);

    return () => {
      rightViewElement && rightViewElement.removeEventListener('scroll', handleScroll);
      leftMapView && leftMapView.removeEventListener('transitionend', handleResizeEndDashboardView);
    };
  }, [search]);

  const applyFilter = (
    revenueRange: { [key: number]: 'visible' | 'hidden' } | undefined = undefined,
    onlyConsiderRevenueFilter: boolean = false,
    filtersToConsider: string[] = []
  ) => {
    logAmplitudeEvent('market_filter_applied');
    setDynamicTrigger(true);
    setTimeout(() => {
      setDynamicTrigger(false);
    }, 500);

    setFilterVal((e: FilterVal) => {
      if (!listings.loading && !otaListings.loading) {
        const [tempFilteredListings, tempFilteredOTAListings] = getFilteredListings(
          listings.data,
          otaListings.data,
          e,
          true,
          revenueRange,
          onlyConsiderRevenueFilter,
          filtersToConsider
        ) as [string[], string[]];

        setFilteredListings(tempFilteredListings);
        setFilteredOTAListings(tempFilteredOTAListings);

        const visibleListings = getVisibleListings(
          listings,
          bounds,
          true,
          tempFilteredListings
        ) as Listing[];

        setVisibleMapListings(visibleListings.slice(0, 22));
        setMapViewportListings({ count: visibleListings.length, loading: false });

        const boundedOTAListings = getBoundedOTAListings(otaListings.data, bounds);
        const visibleOTAListingIDs = new Set(
          getVisibleOTAListings(boundedOTAListings, tempFilteredOTAListings)
        );
      }
      return e;
    });
  };

  const resetRevenuePotentialFilter = () => {
    const newRevPotentialRange = { ...revPotentialRangeInvestment };
    for (const key in newRevPotentialRange) {
      newRevPotentialRange[key] = 'visible';
    }
    dispatch(setIndividualRevenuePotentialRange(newRevPotentialRange));
  };

  const resetFilter = () => {
    setDynamicTrigger(true);
    setTimeout(() => {
      setDynamicTrigger(false);
    }, 500);
    setFilterActive(false);
    if (!listings.loading && !otaListings.loading) {
      const filteredListings = Object.keys(listings.data);
      setFilteredListings(filteredListings);
      setFilteredOTAListings(Object.keys(otaListings.data));
      resetRevenuePotentialFilter();

      const boundedOTAListings = getBoundedOTAListings(otaListings.data, bounds);
      const visibleListings = getVisibleListings(listings, bounds, false, []) as Listing[];
      setVisibleMapListings(visibleListings.slice(0, 22));
      setMapViewportListings({ count: visibleListings.length, loading: false });

      const visibleOTAListingIDs = new Set(getVisibleOTAListings(boundedOTAListings, []));

      setFilterVal(prevFilterVal => ({
        ...prevFilterVal,
        roomType: ['all'],
        bedrooms: [100],
        bathrooms: [100],
        revenueFilter: ['all'],
        priceTierFilter: ['all'],
        accommodates: [1, 16],
        minimum_nights: [1, 30],
        number_of_reviews: 'all',
        rating: [3.9, 5],
        amenities: {
          pool: null,
          parking: null,
          hot_tub: null,
          gym: null,
          pets: null,
          dryer: null,
          washer: null,
          kitchen: null,
          ac: null,
          heating: null,
          wifi: null,
          tv: null,
          bbq_grill: null,
          fireplace: null,
          breakfast: null,
          beachfront: null,
          amazing_views: null,
        },
        hostType: 'both',
      }));

      [
        'room_type',
        'bedroom_number',
        'bathroom_number',
        'no_of_people',
        'host_type',
        'amenities',
        'minimum_nights',
        'number_of_reviews',
        'rating',
      ].forEach(localStorageValue => {
        localStorage.removeItem(localStorageValue);
      });
    }
  };

  const syncDynamicListingsWithVisibleListings = () => {
    setDynamicTrigger(true);

    const visibleListings = getVisibleListings(
      listings,
      bounds,
      filterActive,
      filteredListings as string[]
    ) as Listing[];

    setDynamicVisibleListings({ data: visibleListings, loading: false });

    setDynamicTrigger(false);
    setIsDynamicDataBtnVisible(false);
    trackUserDynamicDataUpdateAttempt();
    logAmplitudeEvent('dynamic_data_discovered');
  };

  useEffect(() => {
    dashboardLayoutView == 'map' && setIsDynamicDataBtnVisible(true);
  }, [filteredListings]);

  const dashboardLoadingScreenFullSize = (
    <Grid container spacing={2} sx={{ padding: '20px' }}>
      <Grid item xs={12}>
        {/* Use the loading animation twice to fill the page */}
        {[1, 2].map(_ => (
          <>
            <Skeleton height={120} sx={{ borderRadius: 4 }} />
            <Skeleton height={60} animation="wave" sx={{ borderRadius: 4 }} />
            <Skeleton height={40} sx={{ borderRadius: 4 }} />
            <Skeleton height={50} animation="wave" sx={{ borderRadius: 4 }} />
          </>
        ))}
      </Grid>
    </Grid>
  );

  const SeasonalityChartMemoed = useMemo(
    () =>
      noDataInMarket ? (
        <NoDataCard height={150} borderRadius={8} />
      ) : listings.data && !Object.keys(listings.data).length && !cachedLTMData ? (
        <LoadingCard height={150} type="bars" />
      ) : (
        <>
          <SeasonalityChart
            metricsHistoricalData={revenueHistoricalData}
            metricKey={'revenue'}
            selectedPercentileInMainKPICard={selectedPercentileInMainKPICard}
            currency={currency}
          />
        </>
      ),
    [noDataInMarket, revenueHistoricalData, currency]
  );

  const { yearlyYoYChange, nextYearPredcition } = calculateHistoricalTrends(
    revenueHistoricalData,
    'revenue',
    selectedPercentileInMainKPICard
  );

  const shouldShowIndividualListingModalOpen = (listingID: string | null) => {
    if (!listingID || !listings.data || !listings.data[listingID]) {
      return false;
    }
    return true;
  };

  const TrendsChartMemo = noDataInMarket ? (
    <NoDataCard height={150} borderRadius={8} />
  ) : listings.data && !Object.keys(listings.data).length && !cachedLTMData ? (
    <LoadingCard height={150} type="bars" />
  ) : (
    <>
      <TrendsChart yearlyYoYChange={yearlyYoYChange} isDisabled={isDisabled} currency={currency} />
    </>
  );

  const MainKPICardMemoed = noDataInMarket ? (
    <NoDataCard height={150} borderRadius={8} />
  ) : shouldShowMainKPILoading ? (
    <LoadingCard height={150} type="bars" />
  ) : (
    <>
      {isCachedMarketDataSample && (
        <Alert severity="info" sx={{ marginBottom: '10px' }}>
          The current Market Overview graph shows data from up to 100 listings. We're working on
          updating it with complete data, which should be ready in 15 minutes. If you don't see the
          update after 15 minutes, please contact support@airbtics.com.
        </Alert>
      )}

      <Box
        sx={{
          width: '100%',
          height: matches ? '500px' : '400px',
          boxShadow: '0 4px 10px #0003',
          borderRadius: '8px',
        }}
      >
        <MainKPICard
          constantDummyData={
            parseMessageHistoricalData(deepCopyObject(dummyMarketHistoricalData))
              .historical_monthly_performance
          }
          currency={currency}
          marketLevelOccupancyRate={marketLevelOccupancyRate}
          occupancyYoYChange={occupancyYoYChange}
          marketLevelRevenuePotential={marketLevelRevenuePotential}
          marketLevelDailyRate={marketLevelDailyRate}
          revenueYoYChange={revenueYoYChange}
          dailyYoYChange={dailyYoYChange}
          marketLevelActiveListings={marketLevelActiveListings}
          marketLevelCleaningFee={marketLevelCleaningFee}
          marketLevelBookingCount={marketLevelBookingCount}
          listingYoYChange={listingYoYChange}
          occupancyRateHistoricalData={occupancyRateHistoricalData}
          occupancyPast6MonthsHistoricalData={occupancyPast6MonthsHistoricalData}
          revenueHistoricalData={revenueHistoricalData}
          revenuePast6MonthsHistoricalData={revenuePast6MonthsHistoricalData}
          dailyRateHistoricalData={dailyRateHistoricalData}
          dailyRatePast6MonthsHistoricalData={dailyRatePast6MonthsHistoricalData}
          listingHistoricalData={listingHistoricalData}
          listingPast6MonthsHistoricalData={listingPast6MonthsHistoricalData}
          matches={matches}
          handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'kpiGraph')}
          search={search}
          selectedPercentile={selectedPercentileInMainKPICard}
          setSelectedpercentile={setSelectedPercentileInMainKPICard}
          SeasonalityChartMemoed={SeasonalityChartMemoed}
          TrendsChartMemo={TrendsChartMemo}
          lastYearMarketTrend={nextYearPredcition}
          isSubMarketDetailsBlured={isSubMarketDetailsBlured}
        />
      </Box>
    </>
  );

  const CapacityFilterCardSingleBar = (
    <SingleBarFilterCard
      title="Capacity"
      catagoryMap={'dynamicRange'}
      tooltipTitleSuffix="Guests"
      isLoading={cachedMarketData.loading || !cachedMarketData.data?.supply_breakdown?.accommodates}
      data={isMarketCachedDataLoaded ? cachedMarketData.data?.supply_breakdown?.accommodates : null}
      metricTabs={['supply']}
      color={COLOR_PRIMARY}
      currency={currency}
      handleOnSampleClick={handleOnSampleClick}
      tooltipText={'Capacity is the number of guests a property can accept.'}
      isGraphSizeSmall={true}
      maxBarSize={13}
      isGraphLocked={false}
      isDisabled={isDisabled}
      totalListingsCount={marketLevelActiveListings}
      parent="listing_by_capacity"
    />
  );

  const ActiveDaysFilterCardSingleBar = (
    <SingleBarFilterCard
      title="Days Available"
      catagoryMap={{
        // '1-90': { name: '1-90' },
        '90-180': { name: '90-180' },
        '181-270': { name: '181-270' },
        '271-367': { name: '271-365' },
      }}
      tooltipTitleSuffix="days"
      isLoading={
        cachedMarketData.loading || !cachedMarketData.data?.supply_breakdown?.active_days_count
      }
      data={
        isMarketCachedDataLoaded ? cachedMarketData.data?.supply_breakdown.active_days_count : null
      }
      metricTabs={['supply']}
      color={COLOR_PRIMARY_1_700}
      currency={currency}
      handleOnSampleClick={handleOnSampleClick}
      maxBarSize={13}
      isGraphSizeSmall={true}
      xAxisTitle={'days'}
      tooltipText={
        'Days available is the number of days an Airbnb listing was available for booking in the last 12 months.'
      }
      isGraphLocked={false}
      isDisabled={isDisabled}
      totalListingsCount={marketLevelActiveListings}
      parent="listing_by_days_available"
    />
  );

  const PropertySizeCardMemoed = (
    <PropertySizeCard
      listings={filterActiveListings(staticListings, 'obj') as VisibleListingsProp}
      filterVal={filterVal}
      cachedData={isMarketCachedDataLoaded ? cachedMarketData.data?.supply_breakdown.bedroom : null}
      viewMode={dashboardLayoutView}
      isDynamicLoading={dynamicLoadingMapViewKPI}
      multipleMarkersCallLoading={multipleMarkersCallLoading}
      noDataInMarket={noDataInMarket}
      totalListingsCount={marketLevelActiveListings}
    />
  );

  const PropertyTypeChannelCancelationPolicyPieChat = (
    <PropertyTypeChannelPieChart
      listings={filterActiveListings(staticListings, 'obj') as VisibleListingsProp}
      otaListings={otaListings}
      cachedData={
        isMarketCachedDataLoaded
          ? {
              property_type: cachedMarketData.data?.supply_breakdown.property_type,
              cancellation_policy: cachedMarketData.data?.supply_breakdown.cancellation_policy,
              // channel_type: cachedMarketData.data?.supply_breakdown.channel,
            }
          : null
      }
      viewMode={dashboardLayoutView}
      isDynamicLoading={dynamicLoadingMapViewKPI}
      multipleMarkersCallLoading={multipleMarkersCallLoading}
      noDataInMarket={noDataInMarket}
      totalListingsCount={marketLevelActiveListings}
    />
  );

  const AmenityCardMemoed = (
    <AmenityCard
      listings={filterActiveListings(staticListings, 'obj') as VisibleListingsProp}
      cachedData={isMarketCachedDataLoaded ? cachedMarketData.data?.supply_breakdown.amenity : null}
      viewMode={dashboardLayoutView}
      isDynamicLoading={dynamicLoadingMapViewKPI}
      multipleMarkersCallLoading={multipleMarkersCallLoading}
      noDataInMarket={noDataInMarket}
      totalListingsCount={marketLevelActiveListings}
    />
  );

  const ExtraPricingCardMemoed = (
    <ExtraPricingCard
      listings={filterActiveListings(staticListings, 'obj') as VisibleListingsProp}
      currency={currency}
      cachedData={isMarketCachedDataLoaded ? cachedMarketData.data?.extra_pricing : null}
      viewMode={dashboardLayoutView}
      isDynamicLoading={dynamicLoadingMapViewKPI}
      multipleMarkersCallLoading={multipleMarkersCallLoading}
      noDataInMarket={noDataInMarket}
    />
  );

  const TopCitiesCardMemoed = graph.loading ? (
    <LoadingCard height={150} type="bars" />
  ) : (
    <TopCitiesCard
      graph={graph}
      isDisabled={isDisabled}
      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'guestOriginGraphs')}
      matches={matches}
      userTailorMarketAccess={userTailorMarketAccess}
    />
  );

  const TopCountriesCardMemoed = graph.loading ? (
    <LoadingCard height={150} type="bars" />
  ) : (
    <TopCountriesCard
      graph={graph}
      isDisabled={isDisabled}
      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'guestOriginGraphs')}
      matches={matches}
      userTailorMarketAccess={userTailorMarketAccess}
      tooltipSuffix={' of total guests'}
    />
  );

  const GuestLanguageCardMemoed = graph.loading ? (
    <LoadingCard height={150} type="bars" />
  ) : (
    <GuestLanguageCard
      graph={graph}
      isDisabled={isDisabled}
      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'guestOriginGraphs')}
      matches={matches}
      userTailorMarketAccess={userTailorMarketAccess}
      tooltipSuffix={' of total guests'}
    />
  );

  const InternationDomesticGuestsCardMemoed = graph.loading ? (
    <LoadingCard height={350} type="bars" />
  ) : (
    <InternationDomesticGuestsCard
      graph={graph}
      isDisabled={isDisabled}
      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'guestOriginGraphs')}
      userTailorMarketAccess={userTailorMarketAccess}
    />
  );

  const HostsMemoed = noDataInMarket ? (
    <NoDataCard height={150} borderRadius={8} />
  ) : cachedMarketData.loading || !cachedMarketData.data?.top_5_hosts ? (
    <LoadingCard height={150} type="bars" />
  ) : (
    <Hosts
      listings={filterActiveListings(staticListings, 'obj') as VisibleListingsProp}
      currency={currency}
      isDisabled={isDisabled}
      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'hostsTable')}
      multipleMarkersCallLoading={multipleMarkersCallLoading}
      search={search}
      user={user}
      matches={matches}
      handleMarketSidebarClick={handleMarketSidebarClick}
      handleSidebarListItemClick={handleSidebarListItemClick}
      setAdvancedTab={setAdvancedTab}
      dashboardLayoutView={dashboardLayoutView}
      userTailorMarketAccess={userTailorMarketAccess}
      cachedData={cachedMarketData.data?.top_5_hosts}
    />
  );

  const AmenitiesImpactOnRevenueChartMemoed = noDataInMarket ? (
    <NoDataCard height={150} borderRadius={8} />
  ) : cachedMarketData.loading ? (
    <LoadingCard height={150} type="bars" />
  ) : (
    <AmenitiesImpactOnRevenueChart
      revenueImpactOnAmenitiesDistro={
        cachedMarketData?.data?.revenue_breakdown?.revenue_impact_on_amenities
      }
      isDisabled={isDisabled}
      userTailorMarketAccess={userTailorMarketAccess}
      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'revenueByAmenities')}
    />
  );

  const AllFilterButton = (
    <Grid container sx={{ marginTop: '-60px', pointerEvents: 'none' }}>
      <Grid item xs={12}>
        <ZoomButtons size={30} setZoom={setZoom} />
      </Grid>

      <Grid item xs={12}>
        <Button
          className="mapFilterButton"
          size="small"
          onClick={() => toggleDrawer()}
          sx={{
            fontSize: '16',
            background: '#fff',
            color: COLOR_PRIMARY_DARK,
            pointerEvents: 'auto',
          }}
        >
          <FilterAltOutlinedIcon sx={{ fontSize: 18, color: 'inherit' }} />
          All Filters
        </Button>
      </Grid>
    </Grid>
  );

  const DynamicDataUpdateButton = () => {
    return (
      <Button
        sx={{
          backgroundColor: COLOR_PRIMARY_2,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.35)',
          color: COLOR_WHITE,
          borderRadius: 50,
          pointerEvents: 'auto',
          paddingLeft: 3,
          paddingRight: 3,
          paddingTop: 2,
          paddingBottom: 2,
          ':hover': {
            backgroundColor: COLOR_PRIMARY_1,
            color: COLOR_WHITE,
          },
          fontSize: 18,
        }}
        onClick={syncDynamicListingsWithVisibleListings}
      >
        <RefreshIcon sx={{ marginRight: 1, fontSize: 28 }} /> Fetch data For Visible Map Area
      </Button>
    );
  };

  const StatisticBox = ({ label, value, change, conversionFunction, hideKPI, tooltipconfig }) => {
    const upgradeMessage = useUpgradeMesageExpertFeature();
    return (
      <Box style={{ flexBasis: '24%', height: 'auto', overflow: 'hidden' }}>
        <Box
          sx={{
            height: '50px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            color: '#000',
            padding: '3px 10px',
          }}
        >
          <Typography
            sx={{
              fontWeight: '500',
              fontSize: '14px',
              maxWidth: '70%',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {label}
          </Typography>
          <InfoButton
            tooltipContent={<MainKPITooltip tooltipConfig={tooltipconfig} matches={matches} />}
          />
        </Box>
        <Box
          sx={{
            height: '40%',
            width: '100%',
            color: '#000',
            padding: '0px 10px',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: '1000',
              color: KPI_CARD_COLOR,
            }}
          >
            {hideKPI ? (
              <>
                <LockSharpIcon
                  onClick={() => handleOnSampleClick('expert')}
                  style={{ color: COLOR_PRIMARY, fontSize: '30px', cursor: 'pointer' }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: COLOR_PRIMARY_2,
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    textDecoration: 'underline',
                  }}
                  onClick={() => handleOnSampleClick('expert')}
                >
                  {upgradeMessage}
                </Typography>
              </>
            ) : (
              <span>{value ? conversionFunction(value) : '-'}</span>
            )}
          </Typography>
        </Box>
      </Box>
    );
  };

  const revenueTooltipConfig = {
    ...kpiTooltipConfig['revenue'],
    calculation: kpiTooltipConfig['revenue']['calculation'].map(item =>
      item
        .replace(
          '{revenue}',
          exchangeRates[currency].symbol +
            formatCurrency(
              roundNumber(
                convertCurrency(currency, marketLevelRevenuePotential, false, true)
              ).toString()
            )
        )
        .replace(
          '{accommodation_revenue}',
          exchangeRates[currency].symbol +
            formatCurrency(
              roundNumber(
                convertCurrency(currency, accomodationRevenueInTooltip, false, true)
              ).toString()
            )
        )
        .replace(
          '{cleaning_fee_revenue}',
          exchangeRates[currency].symbol +
            formatCurrency(
              roundNumber(
                convertCurrency(currency, cleaningFeeRevenueInTooltip, false, true)
              ).toString()
            )
        )
        .replace(
          '{nightly_rate}',
          exchangeRates[currency].symbol +
            formatCurrency(
              roundNumber(convertCurrency(currency, marketLevelDailyRate, false, true)).toString()
            )
        )
        .replace('{occupancy_rate}', `${marketLevelOccupancyRate}%`)
        .replace('{no_of_bookings}', `${marketLevelBookingCount}`)
        .replace(
          '{cleaning_fee}',
          exchangeRates[currency].symbol +
            formatCurrency(
              roundNumber(convertCurrency(currency, marketLevelCleaningFee, false, true)).toString()
            )
        )
    ),
  };

  const isFeeUserMainKpiMapViewLoading = !isUserAllowed && visibleMapSupply.loading;
  const mainKpiMapViewNoData = noDataInMarket && isUserAllowed;

  const MainKpiMapView =
    isFeeUserMainKpiMapViewLoading ||
    dynamicLoadingMapViewKPI ||
    (!dynamicVisibleListings.data.length && multipleMarkersCallLoading) ? (
      <Grid container item>
        <LoadingCard height={60} type="bars" barCount={1} />
      </Grid>
    ) : mainKpiMapViewNoData ? (
      <NoDataCard height={120} borderRadius={8} />
    ) : (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        {/* Occupancy */}
        <StatisticBox
          label={'Occupancy Rate'}
          value={marketLevelOccupancyRate == -1 ? '_' : marketLevelOccupancyRate}
          change={occupancyYoYChange}
          conversionFunction={value => `${value} %`}
          hideKPI={
            (isDisabled || !['expert', 'scale'].includes(user.subscriptionPlan)) && !isDemoMarket
          }
          tooltipconfig={kpiTooltipConfig.occupancy}
        />
        {/* Revenue */}
        <StatisticBox
          label={'Revenue'}
          value={marketLevelRevenuePotential == -1 ? '_' : marketLevelRevenuePotential}
          change={revenueYoYChange}
          conversionFunction={value =>
            `${currency && exchangeRates[currency].symbol}${formatCurrency(
              String(convertCurrency(currency, value))
            )}`
          }
          hideKPI={
            (isDisabled || !['expert', 'scale'].includes(user.subscriptionPlan)) && !isDemoMarket
          }
          tooltipconfig={revenueTooltipConfig}
        />
        {/* Nightly Rate */}
        <StatisticBox
          label="Nightly Rate"
          value={marketLevelDailyRate == -1 ? '_' : marketLevelDailyRate}
          change={dailyYoYChange}
          conversionFunction={value =>
            `${currency && exchangeRates[currency].symbol}${formatCurrency(
              String(convertCurrency(currency, value))
            )}`
          }
          hideKPI={
            (isDisabled || !['expert', 'scale'].includes(user.subscriptionPlan)) && !isDemoMarket
          }
          tooltipconfig={kpiTooltipConfig.daily}
        />
        {/* Active Listing */}
        <StatisticBox
          label="Active Listings"
          value={isUserAllowed ? marketLevelActiveListings : visibleMapSupply.data}
          change={listingYoYChange}
          conversionFunction={value => formatCurrency(value.toString())}
          hideKPI={false}
          tooltipconfig={kpiTooltipConfig.listing}
        />
      </Box>
    );

  const ListingCardGrid = useMemo(() => {
    const noListingsInView = visibleMapListings.length == 0;
    if (noDataInMarket || !bounds || Object.keys(bounds).length == 0 || noListingsInView) {
      return <NoDataCard height={150} borderRadius={8} />;
    }

    if ((listings.loading && visibleMapListings.length == 0) || mapLoading) {
      return <ListingsGridLoadingCard />;
    }

    let visibleListings = getVisibleListings(
      listings,
      bounds,
      filterActive,
      filteredListings as string[]
    ) as Listing[];

    setMapViewportListings({ count: visibleListings.length, loading: false });

    if (visibleListings.length < 22) {
      visibleListings = visibleMapListings;
    }

    if (!visibleListings || visibleListings.length == 0) {
      return <NoDataCard height={150} borderRadius={8} />;
    }

    return (
      <Grid
        item
        container
        spacing={1}
        justifyContent={'center'}
        xs={12}
        sx={{
          position: 'relative',
          height: '100%',
          overflow: 'auto',
          overflowX: 'clip',
          marginTop: '0px',
          paddingBottom: '10px',
          marginBottom: '10px',
        }}
      >
        {sortListingBasedOnKey(visibleListings, listingSortingKey)
          .slice(listingsGridStartIndexOffset * 9, listingsGridStartIndexOffset * 9 + 9)
          .map(data => {
            const bedroom = data.bedrooms.toLowerCase() == 'studio' ? 0 : data.bedrooms;
            const referenceADR = cachedMarketBedroomADR ? cachedMarketBedroomADR[bedroom] : null;
            return (
              <Grid
                container
                item
                xs={12}
                md={6}
                lg={4}
                xl={4}
                onMouseEnter={() => {
                  if (search.data.name)
                    updateDynamoDBForUserVerification(
                      search.data.name,
                      setSubscriptionModalOpen,
                      user
                    );
                }}
              >
                <SimplifiedAirbnbPointerBody
                  data={data}
                  currency={currency}
                  canDelete={false}
                  isDisabled={isDisabled}
                  deleteListing={() => {}}
                  setOpenFullView={() => {}}
                  isHidden={false}
                  isEstimator={false}
                  setTopPerformingListingOnFocus={setTopPerformingListingOnFocus}
                  setTopPerformingListingModalOpen={setTopPerformingListingModalOpen}
                  handleOnSampleClick={reqPlan =>
                    handleOnSampleClick(reqPlan, 'individualListingsKPI')
                  }
                  referenceADR={referenceADR}
                />
              </Grid>
            );
          })}
        <Grid container item xs={12} justifyContent="center" alignItems="flex-end">
          <Grid item className="notranslate">
            <Pagination
              page={listingsGridStartIndexOffset + 1}
              count={Math.ceil(
                (isUserAllowed ? visibleListings.length : visibleMapSupply.data) / 9
              )}
              variant="outlined"
              onChange={(event, value) => {
                if (!isUserAllowed && value >= 3) {
                  handleOnSampleClick();
                  return;
                }

                setListingsGridStartIndexOffset(value - 1);

                if (showViewingLisingFromPaginationAlert !== 'shown' && value > 2) {
                  setShowViewingLisingFromPaginationAlert('showing');
                }
              }}
              renderItem={item => {
                const { page, ...other } = item;
                const isLocked = page != null && page > 2 && !isUserAllowed;

                return (
                  <Tooltip title={isLocked ? 'Upgrade to view more listings' : ''}>
                    <PaginationItem
                      {...item}
                      sx={{
                        color: isLocked ? COLOR_GREY_800 : 'inherit',
                      }}
                    />
                  </Tooltip>
                );
              }}
            />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <ShowAlertTextBox
            isOpen={showViewingLisingFromPaginationAlert === 'showing'}
            type={'warning'}
            text={
              'Looking for a specific listing? Zoom in on the map, and this list will display only the listings visible in the current map view.'
            }
          />
        </Grid>
      </Grid>
    );
  }, [
    bounds,
    listingsGridStartIndexOffset,
    listingSortingKey,
    currency,
    filteredListings,
    visibleMapListings,
    listings,
    multipleMarkersCallLoading,
    mapLoading,
    visibleMapSupply,
  ]);

  const getSubSectionHeading = (
    title: string | JSX.Element,
    id: string,
    showDownloadButton: boolean = false,
    downloadKey?: any,
    tooltipText?: string,
    filterButton?: JSX.Element
  ) => {
    return (
      <Grid
        container
        item
        xs={12}
        id={id}
        sx={{
          margin: '10px 10px',
          marginLeft: 0,
          padding: '10px auto',
          fontSize: '20px',
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h5" className="marketTitle" sx={{ fontSize: 'inherit' }}>
          <span>{title} </span>
          {tooltipText ? (
            <InfoTooltip
              enterTouchDelay={0}
              leaveTouchDelay={30000}
              title={
                <Card>
                  <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {tooltipText}
                    </Typography>
                  </CardContent>
                </Card>
              }
              style={{ cursor: 'pointer' }}
            >
              <IconButton style={infoIconButton}>
                <InfoOutlinedIcon style={infoIconStyle} />
              </IconButton>
            </InfoTooltip>
          ) : (
            <></>
          )}
        </Typography>
        {filterButton ? filterButton : <></>}
      </Grid>
    );
  };

  const isActiveListingCountHeaderLoading = isUserAllowed
    ? multipleMarkersCallLoading
    : visibleMapSupply.loading || visibleMapSupply.data == null;

  const activeListingCountDataToUse = isUserAllowed
    ? mapViewportListings.count
    : visibleMapSupply.data;

  const ActiveListingCountHeader = getSubSectionHeading(
    <Box sx={{ display: 'flex' }}>
      <span style={{ color: COLOR_PRIMARY, marginRight: '5px' }}>
        {noDataInMarket ? (
          <span>0</span>
        ) : isActiveListingCountHeaderLoading ? (
          <CircularProgress size={20} />
        ) : (
          <span>{formatCurrency(activeListingCountDataToUse + '')}</span>
        )}
      </span>{' '}
      <span>listings: {search.data ? search.data.name : ''}</span>
      <Box sx={{ cursor: 'pointer', marginLeft: '10px' }}>
        <Typography
          variant="body1"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            color: COLOR_PRIMARY_2,
          }}
          onClick={() => {
            logAmplitudeEvent('listing_export_modal_opened');
            setShowListingsExportPage(true);
          }}
        >
          <DownloadIcon sx={{ marginRight: '2px' }} /> <span>Export</span>
        </Typography>
      </Box>
    </Box>,
    'active-listing-heading',
    false,
    null,
    '', //TODO add tooltip here
    <>
      <Box>
        <FormControl>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={listingSortingKey}
            label=""
            onChange={e => {
              logAmplitudeEvent('listing_grid_sorted');
              isUserAllowed && setListingSortingKey(e.target.value);
              !isUserAllowed && handleOnSampleClick();
            }}
            sx={{
              '& .MuiSelect-select': {
                padding: '4px 0px 4px 10px',
              },
              marginRight: '10px',
            }}
          >
            <MenuItem value={'performance'}>Top properties</MenuItem>
            <MenuItem value={'review'}>
              Review Count{' '}
              {!isUserAllowed && (
                <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
              )}
            </MenuItem>
            <MenuItem value={'revenue'}>
              Revenue{' '}
              {!isUserAllowed && (
                <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
              )}
            </MenuItem>
            <MenuItem value={'occupancy'}>
              Occupancy{' '}
              {!isUserAllowed && (
                <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
              )}
            </MenuItem>
            <MenuItem value={'adr'}>
              Daily Rate{' '}
              {!isUserAllowed && (
                <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
              )}
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
    </>
  );

  const ActiveListingsCountHeaderLoading = (
    <Grid
      item
      xs={12}
      sx={{
        margin: '10px 10px',
        marginLeft: 0,
        padding: '10px auto',
        fontSize: '20px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {isUserAllowed ? (
        <Typography variant="h5" className="marketTitle" sx={{ fontSize: 'inherit' }}>
          Fetching listings <span>{search.data ? 'in ' + search.data.name : ''}</span>... Please
          wait while we gather the latest data {<CircularProgress size={20} />}
        </Typography>
      ) : (
        <Skeleton variant="text" sx={{ width: '300px' }} height="50px" />
      )}
    </Grid>
  );

  const FilterTabs = matches ? (
    <Grid item>
      <Button
        className="mapFilterButton"
        size="large"
        onClick={() => toggleDrawer()}
        sx={{
          fontSize: '20',
          background: COLOR_PRIMARY_2,
          color: '#ffffff',
          '&:hover': { backgroundColor: COLOR_PRIMARY_1 },
        }}
      >
        <FilterAltOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />
        All Filters
      </Button>
    </Grid>
  ) : (
    <></>
  );

  const SaveSubMarketButton = search.data.isSubmarket ? (
    <></>
  ) : (
    <Box
      sx={{
        transform: 'scale(0.8) translateX(-15px)',
      }}
    >
      <TooltipedActionButton
        title="Create a new submarket by specifying custom boundaries and filters for detailed market analysis."
        onClick={() => {
          setShowSubmarketCreationModal(true);
          setBoundarySelectionModalOpen(false);
        }}
        icon={FavoriteBorderIcon}
        label="Save Submarket"
        smallLabel={true}
        highlight={true}
      />
    </Box>
  );

  const ListingCountHeader = (
    <div>
      {/* 
        When google translate is in used, if the components are rendered conditionally, it is causing page crash.
        Rendering both of the conponent, conditioning the style so because of any overridding component doesn't crash.
      */}
      <div style={{ ...(!isActiveListingCountHeaderLoading && { display: 'none' }) }}>
        {ActiveListingsCountHeaderLoading}
      </div>
      <div style={{ ...(isActiveListingCountHeaderLoading && { display: 'none' }) }}>
        {ActiveListingCountHeader}
      </div>
    </div>
  );

  const getAppliedFilterOnSubmarket = () => {
    try {
      const filterApplied = search.data.filters;
      if (!filterApplied) return <></>;

      const capitalize = text => text.charAt(0).toUpperCase() + text.slice(1);

      const propertyType = filterApplied.property_type
        ? filterApplied.property_type.map(type => type.split('_').join(' ')).join(', ')
        : '';
      const bedRoomFilterText = filterApplied.bedrooms ? `${filterApplied.bedrooms} Bedrooms` : '';
      const bathroomFilterText = filterApplied.bathrooms
        ? `${filterApplied.bathrooms} Bathrooms`
        : '';

      const accomodationRange =
        filterApplied.accommodates_min || filterApplied.accommodates_max
          ? `Accommodates: ${filterApplied.accommodates_min ||
              '1'} to ${filterApplied.accommodates_max || '16+'}`
          : '';

      const minimumStay =
        filterApplied.min_nights_min || filterApplied.min_nights_max
          ? `Minimum Stay: ${filterApplied.min_nights_min ||
              '1'} to ${filterApplied.min_nights_max || '30+'}`
          : '';

      const professionallyManaged = filterApplied.professionally_managed
        ? filterApplied.professionally_managed == 1
          ? 'Professionally Managed'
          : 'Not Professionally Managed'
        : '';

      const rating =
        filterApplied.rating_min || filterApplied.rating_max
          ? `Rating: ${filterApplied.rating_min || '0★'} to ${filterApplied.rating_max || '5★'}`
          : '';

      const numberOfReview = filterApplied.review_count_min
        ? `Minimum Review Count: ${filterApplied.review_count_min}`
        : '';

      let amenities = '';
      if (filterApplied.amenities) {
        amenities = Object.keys(filterApplied.amenities)
          .filter(amenity => filterApplied.amenities[amenity])
          .map(capitalize)
          .join(', ');
      }

      const appliedFilters = [
        propertyType,
        bedRoomFilterText,
        bathroomFilterText,
        accomodationRange,
        minimumStay,
        professionallyManaged,
        rating,
        numberOfReview,
        amenities,
      ]
        .filter(Boolean)
        .map(capitalize)
        .join(', ');

      return appliedFilters ? (
        <>
          <span style={{ color: COLOR_PRIMARY_1 }}>Filters Applied:</span> {appliedFilters}
        </>
      ) : (
        <></>
      );
    } catch (err) {
      console.log('Error while parsing filter text: ', err);
      return <></>;
    }
  };

  const mobileFullMapStyle = !showHeaderOrBottomBarOnMobileView
    ? { height: 'calc(100vh - var(--navbar-height-mobile))' }
    : {};

  const getGetNavHeader = (
    hideOption: string = '',
    extraStyling: any = {},
    disableInDesktop: boolean = false,
    size: string = ''
  ) =>
    disableInDesktop && !matches ? (
      <></>
    ) : (
      <DashboardNavbar
        overviewDashboardTabs={overviewDashboardTabs}
        dashboardLayoutView={dashboardLayoutView}
        filterTabs={FilterTabs}
        isMobile={matches}
        hideOption={hideOption}
        extraStyling={extraStyling}
        size={size}
      />
    );

  const getMarketOverViewTypeHeaderText = `${
    search.data.isSubmarket ? 'Submarket' : matches ? '' : 'Market'
  } Overview`;

  return (
    <>
      {/* title and meta tags */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          {`Airbnb Data on ${SEOVisibleListingsCount} listings in ${titleCase(
            SEODistrictName
          )} | Airbtics | ${SEOCountryName}${SEOCityName}`}
        </title>
        <meta
          name="description"
          content={`Occupancy rate, daily rate, and monthly revenue data in ${SEODistrictName}. Here's all your short-term rental data to invest Airbnb smarter! `}
        />
      </Helmet>

      <Grid
        container
        direction="row"
        className="dashboard__overview"
        xs={12}
        style={mobileFullMapStyle}
      >
        {/* SEO */}
        {search.data && search.data.marketBlogLink && (
          <a href={search.data.marketBlogLink} className="offscreen">
            {' '}
            {`See annual Airbnb revenue in ${search.data.marketBlogLink.split('/').pop()}`}{' '}
          </a>
        )}

        <Grid
          container
          alignItems={'flex-start'}
          sx={{ width: mapWidth + '%', overflow: 'hidden' }}
          className="leftView"
          id="overviewLeft"
          style={mobileFullMapStyle}
        >
          <Paper id="map" className="mapWrapper" style={mobileFullMapStyle}>
            {(dashboardLayoutView === 'map' || dashboardLayoutView === 'dashboard') &&
              multipleMarkersCallLoading && (
                <Box className="mapLoader__wrapper">
                  <LinearProgress className="linearProgressbar" color="inherit" />
                </Box>
              )}
            {!search.loading && mapWidth !== 0 ? (
              <>
                <Map
                  matches={matches}
                  dashboardLayoutView={dashboardLayoutView}
                  setDashboardLayoutView={setDashboardLayoutView}
                  zoom={zoom}
                  setZoom={setZoom}
                  search={search}
                  setBounds={setBounds}
                  setIsDynamicDataBtnVisible={setIsDynamicDataBtnVisible}
                  listings={listings}
                  otaListings={otaListings}
                  multipleBookingMarkersCallLoading={multipleBookingMarkersCallLoading}
                  isDisabled={isDisabled}
                  isUserAllowed={isUserAllowed}
                  revPotentialRangeInvestment={revPotentialRangeInvestment}
                  subscriptionModalOpen={subscriptionModalOpen}
                  currency={currency}
                  filteredListings={filteredListings}
                  filteredOTAListings={filteredOTAListings}
                  setSubscriptionModalOpen={setSubscriptionModalOpen}
                  handleOnSampleClick={handleOnSampleClick}
                  applyFilter={applyFilter}
                  filterActive={filterActive}
                  setShowPremiumDataDynamic={setShowPremiumDataDynamic}
                  toggleDrawer={toggleDrawer}
                  setFilterVal={setFilterVal}
                  filterVal={filterVal}
                  setFilterActive={setFilterActive}
                  noDataInMarket={noDataInMarket}
                  topPerformingListingOnFocus={topPerformingListingOnFocus}
                  setListingsGridStartIndexOffset={setListingsGridStartIndexOffset}
                  dummyMarketData={allMarketProcessedData ? allMarketProcessedData : []}
                  discoveryMarkets={discoveryMarkets}
                  filteredDiscoveryMarkets={filteredDiscoveryMarketsSet}
                  setDiscoveryMarkets={setDiscoveryMarkets}
                  topPerformingMarketOnFocus={topPerformingMarketOnFocus}
                  setTopPerformingMarketOnFocus={setTopPerformingMarketOnFocus}
                  marketsToCompareList={marketsToCompareList}
                  setMarketsToCompareList={setMarketsToCompareList}
                  minROI={minROI}
                  maxROI={maxROI}
                  isMarketDataAccessible={isMarketDataAccessible}
                  freeMarketDataKey={freeMarketDataKey}
                  isTabChanging={isTabChanging}
                  selectedDiscoveryCountry={selectedDiscoveryCountry}
                  allRawMarketData={allRawMarketData}
                  setSelectedDiscoveryCountry={setSelectedDiscoveryCountry}
                  mapWidth={mapWidth}
                  openDemoBookingForm={openDemoBookingForm}
                  setDemoFormHeadingFor={setDemoFormHeadingFor}
                  visibleMapListings={visibleMapListings}
                  setVisibleMapListings={setVisibleMapListings}
                  mapViewportListings={mapViewportListings}
                  setMapViewportListings={setMapViewportListings}
                  setSubmarketPolygonPaths={setSubmarketPolygonPaths}
                  mapLoading={mapLoading}
                  setMapLoading={setMapLoading}
                  multipleMarkersCallLoading={multipleMarkersCallLoading}
                  OnBoardingSearchBox={OnBoardingSearchBox}
                  showViewingLisingFromPaginationAlert={showViewingLisingFromPaginationAlert}
                  setShowViewingLisingFromPaginationAlert={setShowViewingLisingFromPaginationAlert}
                  mapHeightOnMobileView={mapHeightOnMobileView}
                  cachedMarketBedroomADR={cachedMarketBedroomADR}
                  cachedMarketData={cachedMarketData.data}
                />
                {dashboardLayoutView !== 'compare' &&
                  !matches &&
                  !(dashboardLayoutView == 'discovery' && matches) && (
                    <Button
                      variant="contained"
                      className="mapToggleBtn"
                      size="small"
                      onClick={() => {
                        if (mapWidth == 20 && dashboardLayoutView !== 'discovery') {
                          setMapWidth(getSelectedViewWidth('map', matches, false));
                          setDashboardLayoutView('map');
                          logAmplitudeEvent('map_viewed_in_overview', { cta: 'toggle_btn' });
                        } else if (mapWidth == 20 && dashboardLayoutView === 'discovery') {
                          setMapWidth(getSelectedViewWidth('discovery', matches, true));
                          setDashboardLayoutView('discovery');
                          handleOnClickTabLoading();
                        } else if (mapWidth == 100 && dashboardLayoutView === 'discovery') {
                          setMapWidth(getSelectedViewWidth('discovery', matches, false));
                          setDashboardLayoutView('discovery');
                          handleOnClickTabLoading();
                        } else if (mapWidth == MAP_SIZE_MAP_VIEW_DESKTOP || mapWidth == 100) {
                          setMapWidth(getSelectedViewWidth('dashboard', matches, false));
                          setDashboardLayoutView('dashboard');
                        }
                      }}
                    >
                      <ArrowRightRoundedIcon
                        className={`mapToggleBtn__icon ${
                          dashboardLayoutView === 'map' || mapWidth >= MAP_SIZE_MAP_VIEW_DESKTOP
                            ? 'mapToggleBtn__icon--active'
                            : ''
                        }`}
                      />
                    </Button>
                  )}

                {matches && (
                  <>
                    <IconButton
                      onClick={() => setDashboardLayoutView('dashboard')}
                      sx={{
                        position: 'absolute',
                        top: '45px',
                        right: '5px',
                        cursor: 'pointer',
                        zIndex: APP_BAR_Z_INDEX,
                        background: '#fff',
                        color: COLOR_RED,
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </>
                )}

                {dashboardLayoutView === 'map' && matches && (
                  <BottomDrawer
                    topComponents={[AllFilterButton]}
                    components={{
                      overview: [
                        <Grid container>
                          <Grid item xs={10}>
                            {getSubSectionHeading(
                              getMapViewHeadings(
                                'Map Area Overview',
                                search.data ? search.data.name : '',
                                OverViewMapSectionMarketStatsTooltip
                              ),
                              'dashboard-overview-heading'
                            )}
                          </Grid>
                          <Grid item xs={2} justifyContent={'flex-end'}>
                            {!isDynamicDataBtnVisible && SaveSubMarketButton}
                          </Grid>
                        </Grid>,
                        MainKpiMapView,
                        <Box sx={{ marginBottom: '50px' }} />,
                      ],
                      listings: [ListingCountHeader && ListingCountHeader, ListingCardGrid],
                    }}
                    isOverLayVisible={isDynamicDataBtnVisible}
                    overlayButton={<DynamicDataUpdateButton />}
                    setMapHeightOnMobileView={setMapHeightOnMobileView}
                  />
                )}
              </>
            ) : (
              <></>
            )}
          </Paper>
        </Grid>
        <Grid
          container
          sx={{ width: 100 - mapWidth + '%' }}
          className="rightView"
          id="overviewRight"
        >
          <Grid
            container
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
            direction={'row'}
            xs={12}
            spacing={1}
            className="rightView__container"
            sx={{ padding: matches ? '0px' : '20px', paddingRight: 0 }}
          >
            {!matches &&
              (dashboardLayoutView === 'dashboard' ||
                dashboardLayoutView === 'map' ||
                dashboardLayoutView === 'compare') && (
                <Grid item xs={12} sx={{ position: 'sticky' }}>
                  {getGetNavHeader()}
                </Grid>
              )}

            {dashboardLayoutView === 'map' && (
              <Filter
                listings={extractVisibleListings(listings)}
                allListings={listings}
                filterVal={filterVal}
                setFilterVal={setFilterVal}
                applyFilter={applyFilter}
                open={filterOpen}
                toggleDrawer={toggleDrawer}
                setFilterActive={setFilterActive}
                matches={matches}
                setFilterApplied={setFilterApplied}
                resetFilter={resetFilter}
                currency={currency}
                revPotentialRangeInvestment={revPotentialRangeInvestment}
                setIndividualRevenuePotentialRange={setIndividualRevenuePotentialRange}
                priceTiers={priceTiers}
                handleOnSampleClick={handleOnSampleClick}
                isListingLoading={!listings.data ? true : isEmpty(listings.data)}
              />
            )}

            {!isTabChanging && dashboardLayoutView === 'map' && !matches ? (
              <Grid container item xs={12}>
                {ListingCountHeader}

                <Grid
                  container
                  item
                  sx={{
                    marginBottom: '10px',
                    borderRadius: '8px',
                    paddingTop: '10px',
                  }}
                >
                  {ListingCardGrid}
                </Grid>

                <Grid
                  container
                  item
                  sx={{
                    height: '190px',
                    width: '100%',
                    marginBottom: '10px',
                    padding: '5px',
                    paddingBottom: '40px',
                    boxShadow: '0 4px 10px #0002, 0 -4px 10px -4px #0001',
                    borderRadius: '8px',
                    position: matches ? 'relative' : 'sticky',
                    bottom: 0,
                    background: '#fff',
                  }}
                >
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ width: '100%', paddingLeft: '10px', paddingTop: '10px' }}
                  >
                    <Grid item>
                      {getMapViewHeadings(
                        'Map Area Overview',
                        search.data ? search.data.name : '',
                        OverViewMapSectionMarketStatsTooltip
                      )}
                    </Grid>
                    <Grid item sx={{ '& .MuiButton-root': { height: '50px !important' } }}>
                      {!isDynamicDataBtnVisible && SaveSubMarketButton}
                    </Grid>
                  </Grid>
                  {isDynamicDataBtnVisible && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '170px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: FILTER_FLOAT_BUTTON_Z_INDEX_DESKTOP - 1,
                      }}
                    >
                      <div
                        style={{
                          position: 'relative',
                          top: '30%',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <DynamicDataUpdateButton />
                      </div>
                    </Box>
                  )}
                  {MainKpiMapView}
                </Grid>
              </Grid>
            ) : (
              <></>
            )}

            {isTabChanging && dashboardLoadingScreenFullSize}

            {dashboardLayoutView === 'dashboard' && !isTabChanging && (
              <Grid container xs={12}>
                <Grid
                  item
                  xs={12}
                  className="section--spacing"
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  wrap="nowrap"
                  spacing={2}
                >
                  <Grid item style={{ paddingLeft: 0 }}>
                    {search.data.isSubmarket && (
                      <Typography
                        variant="h5"
                        className="marketTitle"
                        sx={{ color: COLOR_PRIMARY_1, fontSize: '14px' }}
                      >
                        <Link
                          href={`${window.location.origin}/airbnb-data${search.data.parentMarketUrlPath}`}
                        >
                          {search.data.parentMarketName}
                        </Link>{' '}
                        /{' '}
                        <span style={{ color: COLOR_GREY_400, pointerEvents: 'none' }}>
                          {search.data.name}
                        </span>
                      </Typography>
                    )}
                    <br />
                    <Typography variant="h5" className="marketTitle">
                      {getMarketOverViewTypeHeaderText}: <span>{search.data.name}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ marginTop: '10px' }}>
                      {getAppliedFilterOnSubmarket()}
                    </Typography>
                  </Grid>
                  <Grid item style={{ paddingLeft: 0 }}>
                    <ActionOptions
                      setShowListingsExportPage={setShowListingsExportPage}
                      handleOnSampleClick={handleOnSampleClick}
                      setShowSubmarketCreationModal={setShowSubmarketCreationModal}
                      isCurrentMarketSaved={isCurrentMarketSaved}
                    />
                  </Grid>
                </Grid>

                {getGetNavHeader('map', {}, true, 'small')}

                {/* Main KPI */}
                <Grid item xs={12} className="section--spacing">
                  {MainKPICardMemoed}
                </Grid>

                {OverviewBlurCTA}

                <Grid
                  item
                  container
                  xs={12}
                  ref={subKPIcontainerRef}
                  className="section--spacing"
                  style={{ marginLeft: matches ? 0 : '20px' }}
                >
                  <SubKpiNavbar
                    marketName={search.data?.name}
                    subKpiMetaData={subKpiTabsMetadata}
                    subKpiSelected={subKpiSelected}
                    dashboardScrollPositionTop={dashboardScrollPositionTop}
                    containerRef={subKPIcontainerRef}
                    dashboardLayoutView={dashboardLayoutView}
                    overviewDashboardTabs={overviewDashboardTabs}
                    FilterTabs={FilterTabs}
                    isMobile={matches}
                  />
                </Grid>

                <Grid
                  container
                  item
                  xs={12}
                  className="section--spacing"
                  id="revenue-tab"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="h5" className="marketTitle">
                    Revenue
                  </Typography>
                </Grid>
                <Divider />

                <Grid
                  container
                  alignItems={'flex-start'}
                  item
                  xs={12}
                  className="section--spacing"
                  spacing={2}
                >
                  <Grid item container xs={12} className="subSection--spacing">
                    <ConfigurableFilterBarCard
                      isLoading={filteredListingsDashboard.loading}
                      filteredListingsData={filteredListingsDashboard.data}
                      cachedMonthlyLTMData={cachedMonthlyLTMData}
                      currency={currency}
                      isDisabled={isDisabled}
                      handleOnSampleClick={handleOnSampleClick}
                      listings={listings}
                      setTopPerformingListingModalOpen={setTopPerformingListingModalOpen}
                      setOpenFullView={() => {}}
                      matches={matches}
                      avgMarketRevenue={marketLevelRevenuePotential}
                      avgMarketOccupancyRate={marketLevelOccupancyRate}
                      avgMarketAdr={marketLevelDailyRate}
                      cachedMarketBedroomADR={cachedMarketBedroomADR}
                    />
                  </Grid>

                  <Grid item container xs={12} className="subSection--spacing">
                    <PerformanceHeatmap
                      listings={listings}
                      currency={currency}
                      isLoggedIn={isLoggedIn}
                      handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'heatmap')}
                      matches={matches}
                      isDisabled={isDisabled}
                      userTailorMarketAccess={userTailorMarketAccess}
                    />
                  </Grid>

                  <Grid item container xs={12} className="subSection--spacing">
                    {AmenitiesImpactOnRevenueChartMemoed}
                  </Grid>
                </Grid>

                <Grid
                  container
                  item
                  xs={12}
                  className="section--spacing sectionGroupHeading--spacing"
                >
                  <Typography variant="h5" className="marketTitle" id="listings-tab">
                    Listings
                  </Typography>
                </Grid>

                <Grid
                  container
                  item
                  xs={12}
                  justifyContent={'space-between'}
                  alignItems={'flex-start'}
                  className="section--spacing"
                  spacing={2}
                >
                  <Grid item container xs={matches ? 12 : 6}>
                    <Grid item container className="subSection--spacing">
                      {PropertySizeCardMemoed}
                    </Grid>
                    <Grid item container className="subSection--spacing">
                      {AmenityCardMemoed}
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    container
                    xs={matches ? 12 : 6}
                    justifyContent={'flex-start'}
                    alignItems={'flex-start'}
                  >
                    {PropertyTypeChannelCancelationPolicyPieChat}
                  </Grid>
                  <Grid item container xs={12} sm={6} className="subSection--spacing">
                    {CapacityFilterCardSingleBar}
                  </Grid>
                  <Grid item container xs={12} sm={6} className="subSection--spacing">
                    {ActiveDaysFilterCardSingleBar}
                  </Grid>
                </Grid>

                <Grid
                  container
                  item
                  xs={12}
                  className="section--spacing sectionGroupHeading--spacing"
                  id="nightly-rate-tab"
                >
                  <Typography variant="h5" className="marketTitle">
                    Nightly Rate
                  </Typography>
                </Grid>

                <Grid
                  container
                  item
                  xs={12}
                  justifyContent={'flex-start'}
                  alignItems={'flex-start'}
                  className="section--spacing"
                  spacing={1}
                >
                  <Grid
                    item
                    container
                    xs={matches ? 12 : 6}
                    justifyContent={'flex-start'}
                    alignItems={'flex-start'}
                    className="subSection--spacing"
                  >
                    {ExtraPricingCardMemoed}
                  </Grid>
                </Grid>

                <Grid
                  container
                  item
                  xs={12}
                  className="section--spacing sectionGroupHeading--spacing"
                  id="occupancy-tab"
                >
                  <Typography variant="h5" className="marketTitle">
                    Occupancy Rate
                  </Typography>
                </Grid>

                <Grid
                  container
                  item
                  xs={12}
                  justifyContent={'flex-start'}
                  alignItems={'flex-start'}
                  className="section--spacing"
                  spacing={1}
                >
                  <Grid
                    item
                    container
                    xs={12}
                    justifyContent={'flex-start'}
                    alignItems={'flex-start'}
                  >
                    {InternationDomesticGuestsCardMemoed}
                  </Grid>

                  <Grid
                    item
                    container
                    direction={'row'}
                    xs={12}
                    spacing={2}
                    justifyContent={'flex-start'}
                    alignItems={'flex-start'}
                  >
                    <Grid
                      item
                      xs={matches ? 12 : 4}
                      sx={{ marginBottom: 2 }}
                      className="subSection--spacing"
                    >
                      {TopCitiesCardMemoed}
                    </Grid>

                    <Grid item xs={matches ? 12 : 4} className="subSection--spacing">
                      {TopCountriesCardMemoed}
                    </Grid>

                    <Grid item xs={matches ? 12 : 4} className="subSection--spacing">
                      {GuestLanguageCardMemoed}
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  container
                  item
                  xs={12}
                  className="section--spacing sectionGroupHeading--spacing"
                  id="host-tab"
                >
                  <Typography variant="h5" className="marketTitle">
                    Hosts
                  </Typography>
                </Grid>

                <Grid
                  container
                  item
                  xs={12}
                  justifyContent={'flex-start'}
                  alignItems={'flex-start'}
                  className="section--spacing"
                  spacing={1}
                >
                  <Grid item className="subSection--spacing" sx={{ width: '100%' }}>
                    {HostsMemoed}
                  </Grid>
                </Grid>

                <Grid
                  container
                  item
                  xs={12}
                  justifyContent={'center'}
                  alignItems={'flex-start'}
                  className="section--spacing"
                  spacing={1}
                >
                  <Button
                    onClick={() => {
                      setDashboardLayoutView('map');
                    }}
                    sx={{
                      background: COLOR_PRIMARY_1,
                      color: '#fff',
                      padding: '15px 30px',
                      marginBottom: matches ? '140px' : '20px',
                      transition: 'all 0.25s ease',
                      cursor: 'pointer',
                      fontSize: '20px',
                      '&:hover': {
                        background: COLOR_PRIMARY_1 + '44',
                        color: COLOR_PRIMARY_1,
                      },
                    }}
                  >
                    Analyze Listings in the map
                  </Button>
                </Grid>
              </Grid>
            )}

            {dashboardLayoutView === 'discovery' && !isTabChanging && discoveryMarkets && (
              <DiscoveryTab
                discoveryMarkets={processedMarketData ? processedMarketData : []}
                setTopPerformingMarketOnFocus={setTopPerformingMarketOnFocus}
                marketsToCompareList={marketsToCompareList}
                setMarketsToCompareList={setMarketsToCompareList}
                setDashboardLayoutView={setDashboardLayoutView}
                setSelectedSidebarIndex={setSelectedSidebarIndex}
                isMarketDataAccessible={isMarketDataAccessible}
                freeMarketDataKey={freeMarketDataKey}
                currency={currency}
                mapWidth={mapWidth}
                setMapWidth={setMapWidth}
                isDiscoveryMapViewOpen={isDiscoveryMapViewOpen}
                setIsDiscoveryMapViewOpen={setIsDiscoveryMapViewOpen}
                handleOnSampleClick={handleOnSampleClick}
                discoveryMarketType={discoveryMarketType}
                setDiscoveryMarketType={setDiscoveryMarketType}
                allRawMarketData={allRawMarketData}
                discoverTabData={discoverTabData}
                setDiscoveryTabData={setDiscoveryTabData}
                selectedDiscoveryCountry={selectedDiscoveryCountry}
                setSelectedDiscoveryCountry={setSelectedDiscoveryCountry}
                isDiscoveryMarketLoading={isDiscoveryMarketLoading}
                handleMarketSidebarClick={handleMarketSidebarClick}
                handleSidebarListItemClick={handleSidebarListItemClick}
                setAdvancedTab={setAdvancedTab}
              />
            )}

            {/* {dashboardLayoutView === 'discovery' && !isTabChanging && user.showDiscoveryAI && (
              <DiscoveryAi handleOnSampleClick={handleOnSampleClick} />
            )} */}

            {dashboardLayoutView === 'compare' && (
              <MarketComparePage
                user={user}
                currency={currency}
                isLoggedIn={isLoggedIn}
                dummyMarket={dummyMarket}
                handleOnSampleClick={handleOnSampleClick}
                OnBoardingSearchBox={OnBoardingSearchBox}
                setSubscriptionModalOpen={setSubscriptionModalOpen}
                isMobileScreen={matches}
                source="market"
                headerNav={() =>
                  getGetNavHeader(
                    'map',
                    {
                      backgroundColor: '#fff0 !important',
                      marginTop: '-20px',
                    },
                    false,
                    'small'
                  )
                }
              />
            )}

            {dashboardLayoutView === 'compare2' && !isTabChanging && discoveryMarkets && (
              <CompareTab
                marketsToCompareList={marketsToCompareList}
                setMarketsToCompareList={setMarketsToCompareList}
                discoveryMarkets={discoveryMarkets ? discoveryMarkets : []}
                allMarketData={allMarketProcessedData ? allMarketProcessedData : []}
                minROI={minROI}
                maxROI={maxROI}
                isMarketDataAccessible={isMarketDataAccessible}
                freeMarketDataKey={freeMarketDataKey}
                currency={currency}
                handleOnSampleClick={handleOnSampleClick}
                handleMarketSidebarClick={handleMarketSidebarClick}
                handleSidebarListItemClick={handleSidebarListItemClick}
                setAdvancedTab={setAdvancedTab}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
      {user && user.userDescription !== 'Trial' && (
        <ConfirmUnlockRegionModal
          open={!isUserAllowed && showSearchCreditDeductWarningModal}
          onConfirm={() => {
            setShowSearchCreditDeductWarningModal(false);
            dispatch(getMarkers(loginCountryCode, true, 1));
          }}
          onCancel={() => setShowSearchCreditDeductWarningModal(false)}
          message="Would you like to unlock this market?"
        />
      )}
      {topPerformingListingModalOpen && (
        <IndividualListingModalV2
          open={true}
          onClose={() => setTopPerformingListingModalOpen(null)}
          data={topPerformingListingModalOpen}
          currency={currency}
          isDisabled={isDisabled}
          setSubscriptionModalOpen={setSubscriptionModalOpen}
          handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'individualListingsKPI')}
          subscriptionModalOpen={subscriptionModalOpen}
          multipleBookingMarkersCallLoading={multipleBookingMarkersCallLoading}
          isEstimator={false}
          openDemoBookingForm={openDemoBookingForm}
          setDemoFormHeadingFor={setDemoFormHeadingFor}
          referenceADR={
            cachedMarketBedroomADR
              ? cachedMarketBedroomADR[
                  (topPerformingListingModalOpen as Listing)?.bedrooms ||
                    (topPerformingListingModalOpen as Listing)?.bedrooms
                ]
              : null
          }
          cachedMarketData={cachedMarketData.data}
        />
      )}
      {showListingsExportPage && (
        <ListingsExportModal
          open={showListingsExportPage}
          setOpen={setShowListingsExportPage}
          matches={matches}
          dataStoreTimePeriodQuery={dataStoreTimePeriodQuery}
          dataStoreTimeGranularityQuery={dataStoreTimeGranularityQuery}
        />
      )}

      {showSubmarketCreationModal && (
        <SubmarketCreationModal
          open={showSubmarketCreationModal}
          setOpen={setShowSubmarketCreationModal}
          matches={matches}
          getTokenThunk={getTokenThunk}
          tailoredRegionMarkers={tailoredRegionMarkers}
          getTailoredRegionMarkers={getTailoredRegionMarkers}
          handleOnSampleClick={handleOnSampleClick}
          allListings={listings}
          filterVal={filterVal}
          setFilterVal={setFilterVal}
          currency={currency}
          revPotentialRangeInvestment={revPotentialRangeInvestment}
          setIndividualRevenuePotentialRange={setIndividualRevenuePotentialRange}
          priceTiers={priceTiers}
          submarketPolygonPaths={submarketPolygonPaths}
          setSubmarketPolygonPaths={setSubmarketPolygonPaths}
          boundarySelectionModalOpen={boundarySelectionModalOpen}
          setBoundarySelectionModalOpen={setBoundarySelectionModalOpen}
        />
      )}

      {showListingsExportPaymentFulfillmentCheckModal && (
        <PaymentFulfillmentCheckModal
          open={showListingsExportPaymentFulfillmentCheckModal}
          setOpen={setShowListingsExportPaymentFulfillmentCheckModal}
          matches={matches}
          status={listingsExportCheckoutStatus}
          successMessage="Your dataset will be prepared within 15 minutes. You'll receive it via email and can also download it from Your Account page."
          failureMessage="Please try again."
        />
      )}
      {extraExportCreditsLoading && (
        <Backdrop
          sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
          open={extraExportCreditsLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <FloatingMapDashboardToggleButton
        overviewDashboardTabs={overviewDashboardTabs}
        dashboardLayoutView={dashboardLayoutView}
        isMobileView={matches}
      />
    </>
  );
};

const FloatingMapDashboardToggleButton = ({
  overviewDashboardTabs,
  dashboardLayoutView,
  isMobileView,
}: {
  overviewDashboardTabs: { label: string; value: string; icon: JSX.Element; onclick: () => void }[];
  dashboardLayoutView: 'map' | 'dashboard' | 'discovery' | 'compare';
  isMobileView: boolean;
}) => {
  if (!isMobileView || dashboardLayoutView !== 'dashboard') return <></>;

  const displayTab = overviewDashboardTabs[0];

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '65px',
        padding: '8px 15px',
        left: '50%',
        transform: 'translateX(-50%)',
        border: '1px solid #fff',
        background: BACKGROUND_COLOR_2,
        color: '#fff',
        borderRadius: '900px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        transition: 'all 0.3s ease',
        '&:hover': {
          background: BACKGROUND_COLOR_1,
        },
      }}
      onClick={displayTab.onclick}
    >
      <Typography variant="body1">{displayTab.label}</Typography>
      {displayTab.icon}
    </Box>
  );
};

const DashboardNavbar = ({
  overviewDashboardTabs,
  dashboardLayoutView,
  filterTabs,
  isMobile,
  hideOption,
  extraStyling,
  size,
}: {
  overviewDashboardTabs: { label: string; value: string; icon: JSX.Element; onclick: () => void }[];
  dashboardLayoutView: 'map' | 'dashboard' | 'discovery' | 'compare';
  filterTabs: any;
  isMobile: boolean;
  hideOption?: string;
  extraStyling?: string;
  size?: string;
}) => {
  const renderTabLabelCondition = () => {
    return dashboardLayoutView !== 'map';
  };

  // if (isMobile) return <></>;

  return (
    <>
      <TabBar
        tabList={overviewDashboardTabs}
        currentTab={dashboardLayoutView}
        color={COLOR_PRIMARY_2}
        renderLabelCondition={renderTabLabelCondition}
        actionButton={filterTabs}
        showActionButton={dashboardLayoutView === 'map'}
        isMobile={isMobile}
        hideOption={hideOption}
        extraStyling={extraStyling}
        size={size}
      />
    </>
  );
};

const SubKpiNavbar = ({
  marketName,
  subKpiMetaData,
  subKpiSelected,
  dashboardScrollPositionTop,
  containerRef,
  dashboardLayoutView,
  overviewDashboardTabs,
  FilterTabs,
  isMobile,
}) => {
  const [isSticky, setIsSticky] = useState<boolean>(false);

  useEffect(() => {
    const rect = containerRef.current?.getBoundingClientRect();

    if (rect && (rect as DOMRect).top < 78) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  }, [dashboardScrollPositionTop, isMobile]);

  return (
    <Box className={`subKpiTabConntainer ${isSticky ? 'subKpiTabConntainer--sticky' : ''}`}>
      {isSticky && (
        <Box sx={{ background: '#fff', fontSize: '18px' }}>
          <Typography
            variant="h5"
            className="marketTitle"
            sx={{
              color: '#0009',
              fontSize: 'inherit',
              position: 'relative',
              top: '12px',
              paddingTop: isMobile ? '2px' : '10px',
              paddingLeft: isMobile ? '16px' : '',
              paddingBottom: '5px',
              marginBottom: isMobile ? '5px' : 0,
              borderBottom: isMobile ? '0.5px solid #0001' : '',
            }}
          >
            Market Overview: <span style={{ color: '#000a' }}>{marketName}</span>
          </Typography>
        </Box>
      )}
      {isSticky && !isMobile && (
        <DashboardNavbar
          overviewDashboardTabs={isMobile ? overviewDashboardTabs.reverse() : overviewDashboardTabs}
          dashboardLayoutView={dashboardLayoutView}
          filterTabs={FilterTabs}
          isMobile={isMobile}
        />
      )}
      <TabBar
        tabList={subKpiMetaData}
        currentTab={subKpiSelected}
        color={COLOR_PRIMARY_2}
        renderLabelCondition={() => true}
        actionButton={FilterTabs}
        showActionButton={false}
        isMobile={isMobile}
      />
    </Box>
  );
};

export default OverviewPage;
